'use client'

import { WorkflowExecutionResult } from '@/components/Workflow/Preview/WorkflowFormProvider'
import { analytics } from '@/lib/analytics'
import { useCurrentBusinessProfileId } from '@/lib/contexts/BusinessProfileContext'
import { executionService } from '@/lib/features/execution/ExecutionService'
import { useGraphic } from '@/lib/features/graphic/GraphicContext'
import { userApi } from '@/lib/features/session/sessionApi'
import { useGetUserQuery } from '@/lib/features/user/userApi'
import { workflowApi } from '@/lib/features/workflow/workflowApi'
import { useAppDispatch, useAppSelector, useAppStore } from '@/lib/hooks'
import { reporter } from '@/lib/reporter'
import {
  ChatItemV2,
  ChatItemV2Assistant,
  ChatItemV2User,
  FrontendCopilotInteraction,
  isToolProgressMessage,
  SendMessageOptions,
} from '@/lib/type'
import {
  convertBackendInteractionsToFrontend,
  convertMessageToFrontend,
  playFinishSound,
  playFullTune,
  showCompletionNotification,
} from '@/lib/utils'
import { copilotToolMappings } from '@/utils/copilotToolMappings'
import { useRouter } from 'next/navigation'
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import {
  AgentType,
  AvailableAgents,
  CopilotAgentContext,
  CopilotInteraction,
  CopilotLoadGraphic,
  CopilotMessage,
  CopilotMessageUser,
  CopilotOpenBillingModal,
  CopilotOpenScheduleModal,
  CopilotResponse,
  CopilotSendMessageRequest,
  CopilotSession,
  CopilotSessionContext,
  RollbackSessionResult,
  RollbackSessionResultSchema,
  UserContext,
} from 'sprites-types/api/v2/copilot'
import {
  NodeExecution,
  WorkflowRefStrict,
  WorkflowVersionUtils,
} from 'sprites-types/api/v2/workflows'
import { WorkflowConfig } from 'sprites-types/workflow/core'
import { v4 as uuidv4 } from 'uuid'
import {
  addInteraction,
  addOrUpdateToolProgress,
  appendToolProgressMessage,
  clearError,
  clearWorkflowExecution,
  completeToolProgress,
  removeFromTypewriterQueue,
  removeTypewriterEffect,
  resetState,
  selectInteractions,
  selectMessages,
  setError,
  setIdle,
  setInteractions,
  setLatestResults,
  setSending,
  setStreaming,
  setToolResultContent,
  setWorkflowExecuting,
  updateInteraction,
  updateToolProgressHeadline,
  updateToolProgressMessage,
  updateTypewriterQueue,
  updateTypewriterState,
} from './ChatSlice'
import { copilotEvents } from './copilotEvents'
import { getInitialMessage, removeInitialMessage } from './copilotInitialMessageStorage'
import { CopilotService, createCopilotService } from './CopilotService'
import { useWorkflowStateManager } from './useWorkflowStateManager'
import { processWorkflowResult } from './workflowResultUtils'
import { WorkflowStateManager } from './workflowStateTypes'

// Whitelisted agents that are available to users - should match backend's WHITELISTED_AGENTS
const WHITELISTED_AGENTS: AgentType[] = [AgentType.META_ADS, AgentType.GOOGLE_ADS, AgentType.SEO]

// Default agent type - should match backend's getDefaultAgentType()
const getDefaultAgentType = (): AgentType => AgentType.META_ADS

export type CopilotStatus = 'idle' | 'sending' | 'streaming' | 'error'

// Workflow execution data structure for results panel
interface WorkflowExecutionData {
  executionId: string
  workflowRef?: string
  workflowConfig?: WorkflowConfig // The workflow config used for this execution
  nodeExecutions: Record<string, NodeExecution>
  outputNodeExecutionIds?: string[] // Which node executions to display
  createdAt: string
}

// Enhanced context state to track all context information
interface CopilotContextState {
  workflowRef?: WorkflowRefStrict
  nodeId?: string
  selectedNodeIds?: string[]
  selectedExecutionIds?: string[]
  nodeExecutions?: Record<string, NodeExecution> // Deprecated: kept for backward compatibility
  latestResults?: any // Store the latest workflow execution results
  workflowExecutionId?: string
  workflowExecutionInputs?: Record<string, any> // Store the workflow execution inputs
  workflowData?: any // Store partial workflow data from workflow_update events
  buildingWorkflow?: {
    workflowRef: WorkflowRefStrict
    partialWorkflow: any
    lastUpdated: string
  } // Store building workflow data separately from query cache
  htmlFullscreen?: {
    content?: string
    url?: string
    filename: string
  }
  workflowExecutions?: Record<string, WorkflowExecutionData> // Structured workflow executions
}

// Event queue item for provider-level event management
interface ProviderEventQueueItem {
  id: string
  event: CopilotResponse
  timestamp: string
  processed: boolean
}

interface CopilotContextType {
  sendMessage: (message: string, options?: SendMessageOptions) => Promise<void>
  messages: ChatItemV2[]
  status: CopilotStatus
  error: string | null
  reset: () => ChatItemV2User | undefined
  rollbackToMessage: (messageId: string) => Promise<RollbackSessionResult | undefined>
  setChatInputContent: (
    content: string,
    files?: { url: string; filename: string; ext: string }[]
  ) => void
  sessionId: string | null
  hasHydrated: boolean
  deleteSession: () => Promise<void>
  // Agent management
  currentAgent: AgentType
  switchAgent: (targetAgent: AgentType, reason?: string) => Promise<boolean>
  availableAgents: typeof AvailableAgents
  // Context-related properties
  context: CopilotContextState
  updateSelectedNodes: (nodeIds: string[]) => void
  updateSelectedExecutions: (executionIds: string[]) => void
  updateWorkflowRef: (workflowRef: WorkflowRefStrict) => void
  loadNodeExecutions: (nodeExecutions: Record<string, NodeExecution>) => void
  setHtmlFullscreen: (data: { content?: string; url?: string; filename: string }) => void
  clearHtmlFullscreen: () => void
  clearAllResults: () => void
  // Loading states
  isLoading: boolean
  // Workflow completion callbacks
  setWorkflowPreviewRef: (ref: any) => void

  // Active typewriter animation - only one at a time
  activeTypewriterMessageId: string | null
  startTypewriterAnimation: (messageId: string) => void
  updateTypewriterProgress: (messageId: string, characterIndex: number) => void
  completeTypewriterAnimation: (messageId: string) => void
  isWorkflowExecuting: boolean
  // Event queue management
  eventQueue: ProviderEventQueueItem[]
  processNextEvent: () => ProviderEventQueueItem | null
  clearEventQueue: () => void

  // Enhanced workflow state management
  workflowState: WorkflowStateManager
  // Backward compatibility - computed from workflowState
  hasActiveWorkflowExecutions: boolean
  showThinkingAnimation: boolean
  // Legacy workflow execution state
  workflowExecution: any
  // Track if current user message was persisted to backend
  isCurrentMessagePersisted: boolean
  // Track if there are active tool operations for interaction-level thinking animation
  hasActiveTools: boolean
  handleWorkflowComplete: (result: any, executionId: string) => void
  onOpenPanel?: (content: any, breadcrumbs: any[]) => void
}

const CopilotContext = createContext<CopilotContextType | null>(null)

interface CopilotProviderProps {
  children: ReactNode
  conversationId?: string
  onSessionIdChange?: (newSessionId: string) => void
  onWorkflowComplete?: (result: any) => void
  onWorkflowError?: (error: string) => void
  onOpenPanel?: (content: any, breadcrumbs: any[]) => void
}

export function CopilotProvider({
  children,
  conversationId,
  onSessionIdChange,
  onWorkflowComplete,
  onWorkflowError,
  onOpenPanel,
}: CopilotProviderProps) {
  const dispatch = useAppDispatch()
  const store = useAppStore()
  const router = useRouter()
  const serviceRef = useRef<CopilotService | null>(null)
  const workflowPreviewRef = useRef<any>(null)
  const pendingWorkflowActionsRef = useRef<Array<{ type: 'complete' | 'error'; data: any }>>([])

  const { data, isLoading: isUserLoading } = useGetUserQuery(undefined, {
    refetchOnMountOrArgChange: true,
    refetchOnReconnect: true,
    refetchOnFocus: true,
  })
  const workspaceId = data?.workspace?.id

  // Get current business profile ID for credential context
  const currentBusinessProfileId = useCurrentBusinessProfileId()

  // Initialize workflow state manager
  const workflowState = useWorkflowStateManager({
    debug: process.env.NODE_ENV === 'development',
    persistState: true,
    storageKey: `workflow-state-${conversationId || 'default'}`,
    onPhaseChange: (phase, previous) => {
      console.log('[CopilotContext] Workflow phase changed:', previous, '->', phase)
    },
    onError: (error, phase) => {
      console.error('[CopilotContext] Workflow error in phase', phase, ':', error)
    },
  })

  const { setInitialGraphic } = useGraphic()

  // Get state from Redux
  const interactions = useAppSelector(selectInteractions)
  const messages = useAppSelector(selectMessages)
  const isStreaming = useAppSelector((state) => state.streamingChat.status === 'streaming')
  const isSending = useAppSelector((state) => state.streamingChat.status === 'sending')
  const errorMessage = useAppSelector((state) => state.streamingChat.errorMessage)
  const latestResults = useAppSelector((state) => state.streamingChat.latestResults)
  const typewriterQueue = useAppSelector((state) => state.streamingChat.typewriterQueue)
  const workflowExecution = useAppSelector((state) => state.streamingChat.workflowExecution)

  // Enhanced local state for context management
  const [contextState, setContextState] = useState<CopilotContextState>({})
  const [sessionId, setSessionId] = useState<string | null>(conversationId || null)
  const [hasHydrated, setHasHydrated] = useState<boolean>(false)
  // Track if current user message was persisted to backend
  const [isCurrentMessagePersisted, setIsCurrentMessagePersisted] = useState<boolean>(false)
  // Agent management state
  const [currentAgent, setCurrentAgent] = useState<AgentType>(getDefaultAgentType())
  // Track if user has manually selected an agent (prevents hydration from overwriting)
  const hasManuallySelectedAgentRef = useRef<boolean>(false)

  // Ref to always have access to the latest context state, as for some reason the contextState is not updated in sendMessage()
  const contextStateRef = useRef<CopilotContextState>(contextState)
  useEffect(() => {
    contextStateRef.current = contextState
  }, [contextState])

  // Helper function to create a new FrontendCopilotInteraction (for user messages)
  const createNewInteraction = useCallback(
    (userMessage: ChatItemV2, interactionId?: string): FrontendCopilotInteraction => {
      return {
        id: interactionId || `interaction_${uuidv4()}`,
        sessionId: sessionId || 'default',
        messages: [userMessage], // Direct use of ChatItemV2 - no conversion needed!
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        context: {
          workflowRef: contextStateRef.current.workflowRef,
          workflowExecutionId: contextStateRef.current.workflowExecutionId,
          latestResults: contextStateRef.current.latestResults,
        },
      }
    },
    [sessionId]
  )

  // Helper function to add a message to the newest interaction
  const addMessageToNewestInteraction = useCallback(
    (message: ChatItemV2) => {
      const newestInteraction = interactions[interactions.length - 1]
      if (newestInteraction) {
        const updatedInteraction: FrontendCopilotInteraction = {
          ...newestInteraction,
          messages: [...newestInteraction.messages, message], // Direct use of ChatItemV2!
          updatedAt: new Date().toISOString(),
          context: {
            workflowRef: contextStateRef.current.workflowRef,
            workflowExecutionId: contextStateRef.current.workflowExecutionId,
            latestResults: contextStateRef.current.latestResults,
          },
        }
        dispatch(updateInteraction(updatedInteraction))
      } else {
        // If no interactions exist, create a new one (fallback case)
        const newInteraction = createNewInteraction(message)
        dispatch(addInteraction(newInteraction))
      }
    },
    [interactions, dispatch, createNewInteraction]
  )

  // Track the current active event for typewriter animation
  const [activeEventId, setActiveEventId] = useState<string | null>(null)
  // Track processed events to prevent re-processing
  const processedEventRef = useRef<Set<string>>(new Set())

  // Event queue for managing incoming events
  const eventQueueRef = useRef<ProviderEventQueueItem[]>([])
  // Simple flag to track if typewriter is blocking
  const isTypewriterActiveRef = useRef<boolean>(false)

  // Track active tool operations for interaction-level thinking animation
  const activeToolOperationsRef = useRef<Set<string>>(new Set())
  const [hasActiveTools, setHasActiveTools] = useState<boolean>(false)

  // Track fetched execution IDs to prevent repeated fetching
  const fetchedExecutionIdsRef = useRef<Set<string>>(new Set())

  // Agent switch tracking for tool completion messages
  const latestAgentSwitchRef = useRef<{
    toAgent: string
    fromAgent: string
    timestamp: string
    toolCallId?: string
  } | null>(null)

  // Track if we received response_complete (indicates natural completion)
  const receivedResponseCompleteRef = useRef<boolean>(false)
  // Track if cancellation was requested (prevents notification on cancelled sessions)
  const wasCancelledRef = useRef<boolean>(false)

  // Compute status from Redux state
  const status: CopilotStatus = errorMessage
    ? 'error'
    : isStreaming
      ? 'streaming'
      : isSending
        ? 'sending'
        : 'idle'

  // Expose isWorkflowExecuting from Redux state
  const isWorkflowExecuting = workflowExecution?.isExecuting ?? false

  // Get the singleton service instance (don't create/destroy on mount/unmount)
  useEffect(() => {
    serviceRef.current = createCopilotService()
    // Note: No cleanup function - we don't disconnect the singleton service
  }, [])

  // Clear state when sessionId changes (critical for preventing message leakage between sessions)
  useEffect(() => {
    dispatch(resetState())
    setContextState({}) // Also clear local context state
    setHasHydrated(false) // Reset hydration flag
  }, [conversationId, dispatch])

  // Helper function to safely update context state
  const safelyUpdateContextState = useCallback(
    (updater: (prev: CopilotContextState) => CopilotContextState) => {
      setContextState((prev) => {
        try {
          return updater(prev)
        } catch (error) {
          console.error('[CopilotContext] Error updating context state:', error)
          // Return previous state on error to prevent corruption
          return prev
        }
      })
    },
    []
  )

  // Helper function to update context state
  const updateContext = useCallback(
    (newContext: CopilotAgentContext) => {
      console.log('[CopilotContext] updateContext called', {
        newContext,
        currentWorkflowRef: contextStateRef.current.workflowRef,
      })

      safelyUpdateContextState((prev) => {
        const updated = { ...prev }

        if (newContext.workflowRef !== undefined) {
          console.log('[CopilotContext] Setting workflowRef from agent context', {
            from: prev.workflowRef,
            to: newContext.workflowRef,
            isChange: prev.workflowRef !== newContext.workflowRef,
          })
          updated.workflowRef = newContext.workflowRef
        }

        if (newContext.nodeId !== undefined) {
          updated.nodeId = newContext.nodeId
          // Only update selectedNodeIds if the user hasn't manually selected multiple nodes
          // This prevents backend updates from overriding user selections
          if (!prev.selectedNodeIds || prev.selectedNodeIds.length <= 1) {
            updated.selectedNodeIds = newContext.nodeId ? [newContext.nodeId] : []
            console.log('[CopilotContext] Updated selectedNodeIds from backend:', {
              from: prev.selectedNodeIds,
              to: updated.selectedNodeIds,
              newNodeId: newContext.nodeId,
            })
          } else {
            console.log('[CopilotContext] Preserved user selection, ignoring backend nodeId:', {
              userSelection: prev.selectedNodeIds,
              ignoredNodeId: newContext.nodeId,
            })
          }
        }

        return updated
      })
    },
    [safelyUpdateContextState]
  )

  // Helper function to handle session context
  const updateSessionContext = useCallback(
    (sessionContext: {
      workflowRef?: string
      workflowExecutionId?: string
      latestWorkflowResults?: any[]
      workflowExecutionStatus?: string
      latestExecutionId?: string
    }) => {
      console.log('[CopilotContext] updateSessionContext called', {
        sessionContext,
        currentWorkflowRef: contextStateRef.current.workflowRef,
        hasWorkflowExecutionId: !!sessionContext.workflowExecutionId,
        workflowExecutionId: sessionContext.workflowExecutionId,
        hasLatestExecutionId: !!sessionContext.latestExecutionId,
        latestExecutionId: sessionContext.latestExecutionId,
      })

      safelyUpdateContextState((prev) => {
        const updated = { ...prev }

        if (sessionContext.workflowRef) {
          console.log('[CopilotContext] Setting workflowRef from session context', {
            from: prev.workflowRef,
            to: sessionContext.workflowRef,
            isChange: prev.workflowRef !== sessionContext.workflowRef,
          })
          updated.workflowRef = sessionContext.workflowRef
        }

        // Handle workflowExecutionId from various sources
        if (sessionContext.workflowExecutionId) {
          updated.workflowExecutionId = sessionContext.workflowExecutionId
        } else if (sessionContext.latestExecutionId) {
          // Fallback to latestExecutionId if workflowExecutionId not present
          updated.workflowExecutionId = sessionContext.latestExecutionId
        } else if ((sessionContext as any).nodeExecutions) {
          // Legacy fallback: try to extract execution ID from old nodeExecutions format
          const nodeExecutions = (sessionContext as any).nodeExecutions
          const firstNodeExecution = Object.values(nodeExecutions)[0] as any
          if (firstNodeExecution && typeof firstNodeExecution === 'object') {
            // Try to find workflow execution ID from node execution data
            console.log(
              '[CopilotContext] Trying to extract workflowExecutionId from legacy nodeExecutions',
              {
                firstNodeExecution,
                keys: Object.keys(nodeExecutions),
              }
            )
          }
        }

        if (sessionContext.latestWorkflowResults) {
          updated.latestResults = sessionContext.latestWorkflowResults
        }

        return updated
      })
    },
    [safelyUpdateContextState]
  )

  // Function to fetch execution data if not already fetched
  const fetchExecutionDataIfNeeded = useCallback(
    (workflowExecutionId: string, source: string = 'unknown') => {
      // Check if we've already fetched this execution ID
      if (fetchedExecutionIdsRef.current.has(workflowExecutionId)) {
        console.log(
          `[CopilotContext] Execution data already fetched for ${workflowExecutionId} (${source})`
        )
        return
      }

      console.log(`[CopilotContext] Fetching execution data for ${workflowExecutionId} (${source})`)

      // Mark as fetching to prevent duplicates
      fetchedExecutionIdsRef.current.add(workflowExecutionId)

      executionService
        .getExecutionData(workflowExecutionId)
        .then(async (executionResponse) => {
          console.log(
            `[CopilotContext] Fetched execution data for ${workflowExecutionId} (${source})`,
            {
              nodeExecutionCount: Object.keys(executionResponse.nodeExecutions).length,
            }
          )

          // Update workflow state with node executions
          Object.entries(executionResponse.nodeExecutions).forEach(([executionId, execution]) => {
            // Ensure executionId field is properly set
            const nodeExecution = {
              ...execution,
              executionId: execution.executionId || executionId,
            } satisfies NodeExecution

            console.log(`[CopilotContext] Processing node execution: ${executionId}`, {
              hasExecutionId: !!execution.executionId,
              nodeId: execution.nodeId,
              finalExecutionId: nodeExecution.executionId,
            })

            workflowState.updateNodeExecution(executionId, nodeExecution)
          })

          // Create corrected nodeExecutions with proper executionId fields
          const correctedNodeExecutions: Record<string, NodeExecution> = {}
          Object.entries(executionResponse.nodeExecutions).forEach(([executionId, execution]) => {
            correctedNodeExecutions[executionId] = {
              ...execution,
              executionId: execution.executionId || executionId,
            } satisfies NodeExecution
          })

          // Fetch workflow config if workflowRef is available
          let workflowConfig: WorkflowConfig | undefined
          if (executionResponse.executionData.workflowRef) {
            try {
              const workflowResult = await store.dispatch(
                workflowApi.endpoints.getWorkflow.initiate(
                  executionResponse.executionData.workflowRef
                )
              )
              if (workflowResult.data) {
                workflowConfig = workflowResult.data.data
              }
            } catch (error) {
              console.warn(
                `[CopilotContext] Failed to fetch workflow config for ${executionResponse.executionData.workflowRef}`,
                error
              )
            }
          }

          // Create workflow execution data structure
          const executionData: WorkflowExecutionData = {
            executionId: workflowExecutionId,
            workflowRef: executionResponse.executionData.workflowRef,
            workflowConfig,
            nodeExecutions: correctedNodeExecutions,
            outputNodeExecutionIds: executionResponse.executionData.outputNodeExecutionIds,
            createdAt: executionResponse.executionData.createdAt,
          }

          // Update context state with structured execution data
          safelyUpdateContextState((prev) => ({
            ...prev,
            // Keep nodeExecutions for backward compatibility with other components
            nodeExecutions: {
              ...(prev.nodeExecutions || {}),
              ...correctedNodeExecutions,
            },
            // Store as structured workflow execution
            workflowExecutions: {
              ...(prev.workflowExecutions || {}),
              [workflowExecutionId]: executionData,
            },
          }))
        })
        .catch((error) => {
          console.error(
            `[CopilotContext] Failed to fetch execution data for ${workflowExecutionId} (${source}):`,
            error
          )
          // Remove from fetched set so we can retry later
          fetchedExecutionIdsRef.current.delete(workflowExecutionId)
        })
    },
    [safelyUpdateContextState, workflowState]
  )

  // Effect to fetch execution data when workflowExecutionId changes
  useEffect(() => {
    console.log('[CopilotContext] workflowExecutionId effect triggered', {
      workflowExecutionId: contextState.workflowExecutionId,
      hasExecutionId: !!contextState.workflowExecutionId,
    })
    if (contextState.workflowExecutionId) {
      fetchExecutionDataIfNeeded(contextState.workflowExecutionId, 'context-change')
    }
  }, [contextState.workflowExecutionId, fetchExecutionDataIfNeeded])

  // Workflow completion callbacks that use the ref
  const handleWorkflowComplete = useCallback(
    (result: any, executionId?: string) => {
      // Use unified processing function
      console.log('Processing result', result)
      const processedResult = processWorkflowResult(result)
      console.log('Processed result', processedResult)

      // Convert to WorkflowExecutionResult format
      const executionResult: WorkflowExecutionResult = {
        success: processedResult.success,
        outputs: processedResult.outputs,
        nodeResults: processedResult.nodeResults,
        summary: processedResult.summary,
        message: processedResult.message,
        executionId: executionId,
      }

      workflowState.completeExecution(executionResult)

      // Maintain backward compatibility with ref-based approach
      if (workflowPreviewRef.current) {
        workflowPreviewRef.current.completeWorkflow(result)
      } else {
        console.log(
          '[CopilotContext] workflowPreviewRef not ready, queuing completion action for when component mounts'
        )
        pendingWorkflowActionsRef.current.push({ type: 'complete', data: result })
      }

      // Call the optional callback
      if (onWorkflowComplete) {
        onWorkflowComplete(result)
      }
    },
    [onWorkflowComplete, workflowState]
  )

  const handleWorkflowError = useCallback(
    (error: string) => {
      console.log('CopilotContext: handleWorkflowError called with error:', error)

      // Update unified workflow state
      workflowState.errorExecution(error)

      // Maintain backward compatibility with ref-based approach
      if (workflowPreviewRef.current) {
        workflowPreviewRef.current.errorWorkflow(error)
      } else {
        console.log(
          '[CopilotContext] workflowPreviewRef not ready, queuing error action for when component mounts'
        )
        pendingWorkflowActionsRef.current.push({ type: 'error', data: error })
      }

      // Call the optional callback
      if (onWorkflowError) {
        onWorkflowError(error)
      }
    },
    [onWorkflowError, workflowState]
  )

  // Process events from queue
  const processQueuedEvents = () => {
    let processedAnyEvent = false

    // Find and process all non-blocked events
    const unprocessedEvents = eventQueueRef.current.filter((item) => !item.processed)

    for (const eventItem of unprocessedEvents) {
      // Only block typewriter-sensitive events (assistant messages) when typewriter is active
      const isTypewriterSensitive =
        eventItem.event.type === 'message' && eventItem.event.message?.role === 'assistant'

      if (isTypewriterSensitive && isTypewriterActiveRef.current) {
        // continue // Skip this event but continue processing others
      }

      // Process the event
      eventItem.processed = true // Mark as processed BEFORE processing
      processStreamingEvent(eventItem.event)
      processedAnyEvent = true

      // Only process one typewriter-sensitive event at a time
      if (isTypewriterSensitive) {
        break
      }
    }

    // Continue processing if we processed any events or if there are still unprocessed ones
    if (
      processedAnyEvent ||
      unprocessedEvents.some(
        (item) =>
          !(item.event.type === 'message' && item.event.message?.role === 'assistant') ||
          !isTypewriterActiveRef.current
      )
    ) {
      setTimeout(processQueuedEvents, 10)
    }
  }

  // Event queue management functions
  const addEventToQueue = (event: CopilotResponse) => {
    const queueItem: ProviderEventQueueItem = {
      id: event.id || `event_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`,
      event,
      timestamp: event.timestamp || new Date().toISOString(),
      processed: false,
    }

    // Check if event already exists
    const existingIndex = eventQueueRef.current.findIndex((item) => item.id === queueItem.id)
    if (existingIndex !== -1) {
      // For node_execution_chunk events, replace existing with newer data instead of skipping
      if (event.type === 'node_execution_chunk') {
        // Type-safe access since we already confirmed event.type === 'node_execution_chunk'
        eventQueueRef.current[existingIndex] = queueItem
        // CRITICAL FIX: Remove from processed events so the updated event can be processed
        processedEventRef.current.delete(queueItem.id)
        // Mark the queue item as unprocessed so it gets reprocessed
        eventQueueRef.current[existingIndex].processed = false
      } else {
        // For other event types, skip adding if already exists
        return
      }
    } else {
      eventQueueRef.current.push(queueItem)
    }

    // Always try to process - the processQueuedEvents function will handle typewriter blocking logic
    setTimeout(processQueuedEvents, 0)
  }

  const processNextEvent = (): ProviderEventQueueItem | null => {
    const nextEvent = eventQueueRef.current.find((item) => !item.processed)
    return nextEvent || null
  }

  const clearEventQueue = () => {
    eventQueueRef.current = []
    processedEventRef.current.clear()
    isTypewriterActiveRef.current = false
  }

  const removeEventFromQueue = (eventId: string) => {
    eventQueueRef.current = eventQueueRef.current.filter((item) => item.id !== eventId)
    processedEventRef.current.delete(eventId)
  }

  // Helper functions for managing active tool operations
  const addActiveToolOperation = (toolCallId: string) => {
    activeToolOperationsRef.current.add(toolCallId)
    setHasActiveTools(activeToolOperationsRef.current.size > 0)
  }

  const removeActiveToolOperation = (toolCallId: string) => {
    activeToolOperationsRef.current.delete(toolCallId)
    setHasActiveTools(activeToolOperationsRef.current.size > 0)
  }

  const clearAllActiveToolOperations = () => {
    activeToolOperationsRef.current.clear()
    setHasActiveTools(false)
  }

  // Helper function to detect credit-related errors that should not be reported to Sentry/PostHog
  const isCreditRelatedError = (errorMessage: string): boolean => {
    const creditErrorPatterns = [
      'insufficient credits',
      'out of credits',
      'not enough credits',
      'quota exceeded',
      'billing limit',
      'subscription expired',
      'upgrade required',
      'credits for this request',
      'workflow paused due to insufficient credits',
    ]

    const lowerCaseError = errorMessage.toLowerCase()
    return creditErrorPatterns.some((pattern) => lowerCaseError.includes(pattern))
  }

  // Helper function to get tool result data from messages
  const getToolResultData = useCallback(
    (toolCallId: string): any => {
      try {
        // Access messages from Redux store
        const allMessages = interactions.flatMap((interaction) => interaction.messages)
        const toolMessages = allMessages.filter(
          (m) => m.role === 'tool' && 'tool_call_id' in m && m.tool_call_id === toolCallId
        )
        const latestResult = toolMessages[toolMessages.length - 1]
        return latestResult && 'content' in latestResult ? JSON.parse(latestResult.content) : null
      } catch (error) {
        console.warn('Failed to parse tool result:', error)
        return null
      }
    },
    [interactions]
  )

  // Dynamic tool completion message generation
  const getToolCompletionMessage = useCallback(
    (event: any, displayName: string): string => {
      const toolMapping = copilotToolMappings[event.tool as keyof typeof copilotToolMappings]

      if (toolMapping?.customCompletedMessage) {
        try {
          const toolResult = getToolResultData(event.tool_call_id)
          // Special handling for transferAgent - use agent switch info instead of toolResult
          if (event.tool === 'transferAgent' && latestAgentSwitchRef.current) {
            return toolMapping.customCompletedMessage(event, latestAgentSwitchRef.current)
          }
          return toolMapping.customCompletedMessage(event, toolResult)
        } catch (error) {
          console.warn(`Custom completed message function failed for ${event.tool}:`, error)
        }
      }

      // Fallback to generic message
      return `Finished ${displayName}`
    },
    [getToolResultData]
  )

  // Dynamic tool failure message generation
  const getToolFailureMessage = useCallback((event: any, displayName: string): string => {
    const toolMapping = copilotToolMappings[event.tool as keyof typeof copilotToolMappings]

    if (toolMapping?.customFailedMessage) {
      try {
        return toolMapping.customFailedMessage(event, event.error_message)
      } catch (error) {
        console.warn(`Custom failed message function failed for ${event.tool}:`, error)
      }
    }

    // Fallback to generic message
    return `Failed ${displayName}`
  }, [])

  // Type guard for status_change events (not in CopilotResponse union)
  const isStatusChangeEvent = (
    event: unknown
  ): event is { type: 'status_change'; status: string } => {
    return (
      typeof event === 'object' &&
      event !== null &&
      'type' in event &&
      event.type === 'status_change' &&
      'status' in event &&
      typeof event.status === 'string'
    )
  }

  // Centralized event processing function
  const processStreamingEvent = (event: CopilotResponse) => {
    // Prevent re-processing
    if (event.id && processedEventRef.current.has(event.id)) return
    if (event.id) processedEventRef.current.add(event.id)

    switch (event.type) {
      case 'connected':
        // Handle session ID updates
        if (event.sessionId && event.sessionId !== sessionId) {
          setSessionId(event.sessionId)
          if (onSessionIdChange) {
            onSessionIdChange(event.sessionId)
          }
        }
        // Mark streaming as soon as SSE connects (first event), unless we're already streaming or in error
        if (!isStreaming && !errorMessage) {
          dispatch(setStreaming('connected'))
        }
        break

      case 'message':
        if (event.message.role === 'assistant') {
          const messageToAdd: ChatItemV2 = {
            id: event.message.id,
            role: 'assistant',
            type: 'message',
            timestamp: event.timestamp,
            content: event.message.content || '',
            typeWriterState: 0,
            context: event.message.context
              ? {
                  workflowRef: event.message.context.workflowRef,
                  nodeId: event.message.context.nodeId,
                }
              : undefined,
            // Preserve tool call info so the "View Results" panel can show inputs
            ...(event.message.universal_tool_calls
              ? { universal_tool_calls: event.message.universal_tool_calls }
              : {}),
            ...(event.message.tool_calls ? { tool_calls: event.message.tool_calls } : {}),
          }
          addMessageToNewestInteraction(messageToAdd)

          // Only start typewriter animation and update queue if not already running for this message
          if (activeEventId !== event.message.id) {
            setActiveEventId(event.message.id)
            isTypewriterActiveRef.current = true
          }

          if (!typewriterQueue.some((m: ChatItemV2) => m.id === event.message.id)) {
            // Add to typewriter queue
            const assistantMessage: ChatItemV2Assistant = {
              id: event.message.id,
              role: 'assistant',
              type: 'message',
              timestamp: event.timestamp,
              content: event.message.content || '',
              typeWriterState: 0,
              context: event.message.context,
            }
            dispatch(updateTypewriterQueue([assistantMessage]))
          }

          // Update local context for assistant messages
          if (event.message.context) {
            updateContext(event.message.context)
          }
        } else if (event.message.role === 'system') {
          // Handle system messages including compact breakpoints
          const convertedMessage = convertMessageToFrontend(event.message)
          if (convertedMessage) {
            addMessageToNewestInteraction(convertedMessage)
          }
        } else if (event.message.role === 'tool') {
          const isWorkflowToolResult =
            event.message.tool === 'executeWorkflow' ||
            event.message.tool === 'rerunFromNodeExecution'

          if (isWorkflowToolResult) {
            // Workflow tool results: sync progress item (includes executionId)
            const convertedMessage = convertMessageToFrontend(event.message)
            if (convertedMessage && isToolProgressMessage(convertedMessage)) {
              dispatch(addOrUpdateToolProgress(convertedMessage))
            }
          } else {
            // Non-workflow copilot tool results: only store result content for "View Results" button.
            // Do not call addOrUpdateToolProgress — that would overwrite live progress steps
            // with the empty progressMessages array from the tool result message.
            if (event.message.tool_call_id && event.message.content) {
              try {
                const parsedContent = JSON.parse(event.message.content)
                dispatch(
                  setToolResultContent({
                    tool_call_id: event.message.tool_call_id,
                    content: parsedContent,
                  })
                )
              } catch {
                // Store raw string if not valid JSON
                dispatch(
                  setToolResultContent({
                    tool_call_id: event.message.tool_call_id,
                    content: event.message.content,
                  })
                )
              }
            }
          }

          // Special handling for executeWorkflow results
          if (event.message.tool === 'executeWorkflow') {
            try {
              const toolResult = event.message.content ? JSON.parse(event.message.content) : null
              if (toolResult) {
                if (toolResult.status === 'completed' && handleWorkflowComplete) {
                  handleWorkflowComplete(toolResult, 'TEMP EXECID')
                } else if (toolResult.status === 'failed' && handleWorkflowError) {
                  handleWorkflowError(toolResult.error || 'Workflow failed')
                }
              }
            } catch (error) {
              console.error('[CopilotContext] Error parsing executeWorkflow result:', error)
            }
          }
        }
        break

      // Handle new execution lifecycle events
      case 'execution_started':
        console.log('[CopilotContext] Execution started:', {
          executionId: event.executionId,
          executionType: event.executionType,
          workflowRef: event.workflowRef,
          hasInputs: !!event.inputs,
        })

        // Start new execution (executions now stack until explicitly cleared)
        workflowState.startExecution(
          event.workflowRef,
          event.executionType === 'rerun' ? 'Rerunning workflow...' : 'Executing workflow...'
        )
        safelyUpdateContextState((prev) => ({
          ...prev,
          workflowRef: event.workflowRef,
          workflowExecutionId: event.executionId,
          workflowExecutionInputs: event.inputs,
          latestResults: undefined,
        }))

        // Update session context to persist the execution ID
        updateSessionContext({
          workflowRef: event.workflowRef,
          latestExecutionId: event.executionId,
          workflowExecutionId: event.executionId,
          latestWorkflowResults: undefined,
        })

        dispatch(
          setWorkflowExecuting({
            isExecuting: true,
            executionId: event.executionId, // Use execution ID not tool ID
          })
        )
        break

      case 'execution_completed':
        console.log('[CopilotContext] Execution completed:', {
          executionId: event.executionId,
          workflowRef: event.workflowRef,
        })
        workflowState.updateExecutionProgress(90, 'Execution completed')
        dispatch(clearWorkflowExecution())

        // Fetch execution data to get outputNodeExecutionIds for filtering
        fetchExecutionDataIfNeeded(event.executionId, 'execution_completed')

        // Emit event to trigger auto-switch to results panel
        copilotEvents.emitExecutionCompleted(event.executionId, event.workflowRef)
        break

      case 'execution_failed':
        console.log('[CopilotContext] Execution failed:', {
          executionId: event.executionId,
          workflowRef: event.workflowRef,
          error: event.error,
        })
        workflowState.errorExecution(`Execution failed: ${event.error}`)
        dispatch(clearWorkflowExecution())

        // Fetch execution data to get outputNodeExecutionIds for partial results
        fetchExecutionDataIfNeeded(event.executionId, 'execution_failed')
        break

      case 'clear_results':
        console.log('[CopilotContext] Clear results requested')
        // Clear node executions while preserving the loaded workflow
        workflowState.clearNodeExecutions()
        // Clear fetched execution IDs to allow re-fetching if same workflows run again
        fetchedExecutionIdsRef.current.clear()
        safelyUpdateContextState((prev) => ({
          ...prev,
          nodeExecutions: {},
          latestResults: undefined,
          workflowExecutions: {},
        }))
        break

      // Handle new build lifecycle events
      case 'build_started':
        console.log('[CopilotContext] Build started:', {
          buildId: event.buildId,
          buildType: event.buildType,
          workflowRef: event.workflowRef,
        })
        workflowState.startBuilding(
          event.buildType === 'create'
            ? 'Creating workflow...'
            : event.buildType === 'update'
              ? 'Updating workflow...'
              : 'Deploying workflow...'
        )

        // Update context with workflowRef if available (for updates)
        if (event.workflowRef) {
          safelyUpdateContextState((prev) => ({
            ...prev,
            workflowRef: event.workflowRef,
          }))
        }
        break

      case 'build_completed':
        console.log('[CopilotContext] Build completed:', {
          buildId: event.buildId,
          workflowRef: event.workflowRef,
        })
        workflowState.completeBuild({ success: true })
        // Clear building workflow data from context since building is complete
        safelyUpdateContextState((prev) => ({
          ...prev,
          buildingWorkflow: undefined,
        }))
        break

      case 'build_failed':
        console.log('[CopilotContext] Build failed:', {
          buildId: event.buildId,
          error: event.error,
        })
        workflowState.errorBuild(`Build failed: ${event.error}`)
        break

      case 'workflow_execution_tool_status':
        if (event.type !== 'workflow_execution_tool_status') break
        // Workflow execution tools (executeWorkflow, rerunFromNodeExecution, template tools)
        // with executionId tracking
        const workflowToolDisplayName =
          copilotToolMappings[event.tool as keyof typeof copilotToolMappings]?.displayName ||
          event.tool
        const workflowToolHeadline =
          event.status === 'started'
            ? `Running ${workflowToolDisplayName}`
            : event.status === 'completed'
              ? getToolCompletionMessage(event, workflowToolDisplayName)
              : getToolFailureMessage(event, workflowToolDisplayName)

        if (event.status === 'started') {
          // Track active tool operation
          addActiveToolOperation(event.tool_call_id)

          dispatch(
            addOrUpdateToolProgress({
              id: event.id,
              role: 'tool',
              tool: event.tool,
              tool_call_id: event.tool_call_id,
              status: 'started',
              progressMessages: [],
              expanded: true,
              headline: workflowToolHeadline,
              timestamp: event.timestamp,
              type: 'workflow_execution_tool_status',
              executionId: event.executionId, // Available for "View Results" button
            })
          )
        } else if (event.status === 'completed' || event.status === 'failed') {
          // Remove from active tracking
          removeActiveToolOperation(event.tool_call_id)

          dispatch(
            completeToolProgress({
              tool_call_id: event.tool_call_id,
              status: event.status,
              headline: workflowToolHeadline,
            })
          )

          if (event.status === 'failed') {
            const errorMessage = event.error_message || 'Unknown error'
            console.error(
              `[CopilotContext] Workflow execution tool failed: ${workflowToolDisplayName}`,
              {
                tool: event.tool,
                tool_call_id: event.tool_call_id,
                executionId: event.executionId,
                error_message: errorMessage,
                timestamp: event.timestamp,
              }
            )

            // Add error as a progress message
            dispatch(
              appendToolProgressMessage({
                tool_call_id: event.tool_call_id,
                progress_message: `Error: ${errorMessage}`,
              })
            )
          }
        }
        break

      case 'tool_status':
        if (event.type !== 'tool_status') break
        const displayName =
          copilotToolMappings[event.tool as keyof typeof copilotToolMappings]?.displayName ||
          event.tool
        const headline =
          event.status === 'started'
            ? `Running ${displayName}`
            : event.status === 'completed'
              ? getToolCompletionMessage(event, displayName)
              : getToolFailureMessage(event, displayName)

        // NOTE: Workflow execution state is now handled by execution_* events
        // Tool status events are only for tool-specific UI updates
        if (event.tool === 'executeWorkflow') {
          // Execution state handled by execution_started/completed/failed events
          if (event.status === 'failed') {
            const errorMessage = event.error_message || 'Unknown error'
            console.error(`[CopilotContext] Tool failed: ${displayName}`, {
              tool: event.tool,
              tool_call_id: event.tool_call_id,
              error_message: errorMessage,
              timestamp: event.timestamp,
            })

            // Add error as a progress message
            dispatch(
              appendToolProgressMessage({
                tool_call_id: event.tool_call_id,
                progress_message: `Error: ${errorMessage}`,
              })
            )

            workflowState.errorExecution(`${displayName} failed: ${errorMessage}`)
            dispatch(clearWorkflowExecution())
          }
        } else if (event.tool === 'rerunFromNodeExecution') {
          // Execution state handled by execution_started/completed/failed events
          if (event.status === 'failed') {
            const errorMessage = event.error_message || 'Unknown error'
            console.error(`[CopilotContext] Tool failed: ${displayName}`, {
              tool: event.tool,
              tool_call_id: event.tool_call_id,
              error_message: errorMessage,
              timestamp: event.timestamp,
            })

            // Add error as a progress message
            dispatch(
              appendToolProgressMessage({
                tool_call_id: event.tool_call_id,
                progress_message: `Error: ${errorMessage}`,
              })
            )

            workflowState.errorExecution(`${displayName} failed: ${errorMessage}`)
            dispatch(clearWorkflowExecution())
          }
        } else if (
          event.tool === 'createWorkflow' ||
          event.tool === 'deployWorkflow' ||
          event.tool === 'updateWorkflow' ||
          event.tool === 'buildWorkflow'
        ) {
          // Build state handled by build_started/completed/failed events
          if (event.status === 'failed') {
            const errorMessage = event.error_message || 'Unknown error'
            console.error(`[CopilotContext] Tool failed: ${displayName}`, {
              tool: event.tool,
              tool_call_id: event.tool_call_id,
              error_message: errorMessage,
              timestamp: event.timestamp,
            })

            // Add error as a progress message
            dispatch(
              appendToolProgressMessage({
                tool_call_id: event.tool_call_id,
                progress_message: `Error: ${errorMessage}`,
              })
            )

            workflowState.errorBuild(`${displayName} failed: ${errorMessage}`)
          }
        }

        if (event.status === 'started') {
          // Track this tool operation as active
          addActiveToolOperation(event.tool_call_id)

          dispatch(
            addOrUpdateToolProgress({
              id: event.id,
              role: 'tool',
              tool: event.tool,
              tool_call_id: event.tool_call_id,
              status: 'started',
              progressMessages: [],
              expanded: true,
              headline,
              timestamp: event.timestamp,
              type: 'tool_status',
            })
          )
        } else if (event.status === 'completed' || event.status === 'failed') {
          // Remove this tool operation from active tracking
          removeActiveToolOperation(event.tool_call_id)

          dispatch(
            completeToolProgress({
              tool_call_id: event.tool_call_id,
              status: event.status,
              headline,
            })
          )

          // Log any tool failures that weren't handled by specific tool handlers
          if (event.status === 'failed') {
            const errorMessage = event.error_message || 'Unknown error'

            console.error(`[CopilotContext] Tool failed: ${displayName}`, {
              tool: event.tool,
              tool_call_id: event.tool_call_id,
              error_message: errorMessage,
              timestamp: event.timestamp,
            })

            // Add error as a progress message
            dispatch(
              appendToolProgressMessage({
                tool_call_id: event.tool_call_id,
                progress_message: `Error: ${errorMessage}`,
              })
            )
          }

          // Keep current streaming status; do not force idle here
        }
        break

      case 'tool_progress':
        // Update workflow state progress if it's an executeWorkflow tool
        // We can identify this by checking if we're currently executing
        if (workflowState.isExecuting && event.progress_message) {
          // Extract progress percentage if available in the message
          const progressMatch = event.progress_message.match(/(\d+)%/)
          const progressPercentage = progressMatch ? parseInt(progressMatch[1], 10) : undefined

          if (progressPercentage !== undefined) {
            workflowState.updateExecutionProgress(progressPercentage, event.progress_message)
          } else {
            // Update message without changing percentage
            workflowState.updateExecutionProgress(
              workflowState.progress.percentage,
              event.progress_message
            )
          }
        }

        // If this is an executeWorkflow progress message that includes a user-friendly
        // execution string like "Executing <workflowName>...", promote it to the
        // headline for the associated tool progress entry so the UI can render it
        // without needing to inspect individual progress messages.
        if (
          workflowState.isExecuting &&
          event.progress_message &&
          event.progress_message.startsWith('Executing ') &&
          event.tool_call_id
        ) {
          dispatch(
            updateToolProgressHeadline({
              tool_call_id: event.tool_call_id,
              headline: event.progress_message,
            })
          )
        }

        dispatch(
          appendToolProgressMessage({
            tool_call_id: event.tool_call_id,
            progress_message: event.progress_message,
            message_id: event.message_id || event.id, // Use event.message_id if available, fallback to event.id
            hidden: event.hidden,
            icon: event.icon,
          })
        )
        break

      case 'tool_progress_update':
        dispatch(
          updateToolProgressMessage({
            tool_call_id: event.tool_call_id,
            message_id: event.message_id,
            new_content: event.new_content,
            hidden: event.hidden,
            icon: event.icon,
          })
        )
        break

      case 'node_execution_chunk':
        // Update individual node execution in workflow state
        if (event.nodeExecution && event.nodeExecution.nodeId) {
          workflowState.updateNodeExecution(event.nodeExecution.executionId, event.nodeExecution)

          // Also update local context with node execution - indexed by executionId for DAG ordering
          if (event.workflowRef && event.nodeExecution.executionId) {
            // Extract workflow execution ID from node execution ID
            const match = event.nodeExecution.executionId.match(/^node_execution_([0-9a-f-]+)_/)
            const workflowExecId = match ? `workflow_execution_${match[1]}` : null

            safelyUpdateContextState((prev) => ({
              ...prev,
              // Keep nodeExecutions for backward compatibility
              nodeExecutions: {
                ...(prev.nodeExecutions || {}),
                [event.nodeExecution.executionId]: event.nodeExecution,
              },
              // Also update workflowExecutions structure
              workflowExecutions: workflowExecId
                ? {
                    ...(prev.workflowExecutions || {}),
                    [workflowExecId]: {
                      ...(prev.workflowExecutions?.[workflowExecId] || {
                        executionId: workflowExecId,
                        workflowRef: event.workflowRef,
                        nodeExecutions: {},
                        createdAt: new Date().toISOString(),
                      }),
                      nodeExecutions: {
                        ...(prev.workflowExecutions?.[workflowExecId]?.nodeExecutions || {}),
                        [event.nodeExecution.executionId]: event.nodeExecution,
                      },
                    },
                  }
                : prev.workflowExecutions,
            }))
          }

          // Generate progress message if tool_call_id is present
          const eventWithToolCallId = event as typeof event & { tool_call_id?: string }
          if (eventWithToolCallId.tool_call_id) {
            const nodeExec = event.nodeExecution
            let progress_message: string
            let progress_icon: string

            if (nodeExec.status === 'completed') {
              progress_message = `Completed node "${nodeExec.nodeId}"`
              progress_icon = 'checkmark'
            } else if (nodeExec.status === 'failed') {
              const errorDetails = (nodeExec.outputs as any)?.failureReason
                ? `${(nodeExec.outputs as any)?.error || 'Unknown error'} (${(nodeExec.outputs as any)?.failureReason})`
                : (nodeExec.outputs as any)?.error || 'Unknown error'
              progress_message = `Node "${nodeExec.nodeId}" failed: ${errorDetails}`
              progress_icon = 'error'
            } else if (nodeExec.status === 'waiting_approval') {
              progress_message = `Waiting for approval on node "${nodeExec.nodeId}"`
              progress_icon = 'clock'
            } else if (nodeExec.status === 'running') {
              progress_message = `Running node "${nodeExec.nodeId}"`
              progress_icon = 'loading'
            } else {
              progress_message = `Node "${nodeExec.nodeId}" status: ${nodeExec.status}`
              progress_icon = 'info'
            }

            dispatch(
              appendToolProgressMessage({
                tool_call_id: eventWithToolCallId.tool_call_id,
                progress_message,
                message_id: `msg_${Date.now()}_${nodeExec.executionId}`,
                hidden: true,
                icon: progress_icon,
              })
            )
          }
        }
        break

      case 'node_executions':
        // Update all node executions when received
        if (event.nodeExecutions) {
          Object.entries(event.nodeExecutions).forEach(([executionId, execution]) => {
            workflowState.updateNodeExecution(executionId, execution as NodeExecution)
          })

          console.log('Received results', event.result)

          // Also update context with final execution results
          // Merge new executions with existing ones to preserve results from all workflows
          safelyUpdateContextState((prev) => ({
            ...prev,
            nodeExecutions: {
              ...(prev.nodeExecutions || {}),
              ...event.nodeExecutions,
            },
            latestResults: event.result || prev.latestResults,
            workflowExecutionId: event.workflowExecutionId,
          }))
          if (event.result) {
            dispatch(
              setLatestResults({
                workflowRef: event.workflowRef,
                nodeResults: event.result,
                status: event.status,
              })
            )
          }
        }
        break

      case 'workflow_execution':
        // Handle new workflow execution event - store execution ID and let effect fetch data
        if (event.workflowExecutionId) {
          console.log('Received workflow_execution event, storing execution ID...', {
            workflowExecutionId: event.workflowExecutionId,
            status: event.status,
          })

          // Store the execution ID and results in context immediately
          // The useEffect will automatically fetch execution data when workflowExecutionId changes
          safelyUpdateContextState((prev) => ({
            ...prev,
            workflowExecutionId: event.workflowExecutionId,
            latestResults: event.result || prev.latestResults,
          }))

          // Update Redux with results
          if (event.result) {
            dispatch(
              setLatestResults({
                workflowRef: event.workflowRef,
                nodeResults: event.result,
                status: event.status,
              })
            )
          }
        }
        break

      case 'load_workflow':
        // Update workflow context and invalidate cache for all variants of this workflow id
        // Convert null workflowRef to undefined (null means "clear context", undefined means "no workflow")
        safelyUpdateContextState((prev) => ({
          ...prev,
          workflowRef: event.workflowRef || undefined,
          // Clear building workflow data from context since we're loading a complete workflow
          buildingWorkflow: undefined,
        }))
        {
          // Only invalidate workflow cache if we have a valid workflowRef
          if (event.workflowRef) {
            const baseId = WorkflowVersionUtils.getBaseId(event.workflowRef)
            const latestRef = WorkflowVersionUtils.create(baseId, 'latest')
            const legacyId = baseId
            const tags = [event.workflowRef, latestRef, legacyId].map((id) => ({
              type: 'Workflow' as const,
              id,
            }))
            dispatch(workflowApi.util.invalidateTags(tags))
          }
          // Note: null workflowRef explicitly clears context, undefined means no change
        }
        break

      case 'workflow_update':
        // Handle incremental workflow updates during generation
        if (event.workflowRef && event.partialWorkflow) {
          // Store building workflow data separately from query cache for immediate UI access
          safelyUpdateContextState((prev) => ({
            ...prev,
            workflowRef: event.workflowRef || prev.workflowRef,
            buildingWorkflow: {
              workflowRef: event.workflowRef,
              partialWorkflow: event.partialWorkflow,
              lastUpdated: event.timestamp || new Date().toISOString(),
            },
          }))
        }
        break

      case 'integrationSignIn':
        const integrationSignInMessage: ChatItemV2 = {
          id: event.id,
          role: 'assistant',
          type: 'message',
          timestamp: event.timestamp,
          content: 'SIGN IN NOW',
          signInDetails: event.signInDetails,
        }
        addMessageToNewestInteraction(integrationSignInMessage)
        break

      case 'googleDriveFilePicker':
        const filePickerMessage: ChatItemV2 = {
          id: event.id,
          role: 'assistant',
          type: 'message',
          timestamp: event.timestamp,
          content: 'PICK A FILE',
          googleDriveFilePickerInfo: {
            accountId: event.accountId,
            mimeType: event.mimeType,
            limit: event.limit,
          },
        }
        addMessageToNewestInteraction(filePickerMessage)
        break

      case 'load_graphic':
        const loadGraphicMessage = event as CopilotLoadGraphic

        setInitialGraphic(loadGraphicMessage.graphic)

        console.log('[CopilotContext] Load graphic event received:', loadGraphicMessage)
        break

      case 'thread_summary_updated':
        if ('summary' in event && sessionId) {
          console.log('[CopilotContext] Thread summary updated:', event.summary)

          // Update individual session cache
          try {
            dispatch(
              userApi.util.updateQueryData('getSession', sessionId, (draft) => {
                draft.threadSummary = event.summary
              })
            )
          } catch (error) {
            console.debug('[CopilotContext] Session cache not found, skipping update', error)
          }

          // Update all session list caches
          const state = store.getState()
          if (state?.api?.queries) {
            Object.keys(state.api.queries).forEach((key) => {
              if (key.startsWith('getAllUserSessions(')) {
                try {
                  const match = key.match(/getAllUserSessions\((.*?)\)/)
                  if (match) {
                    const params = match[1] ? JSON.parse(match[1]) : undefined
                    dispatch(
                      userApi.util.updateQueryData('getAllUserSessions', params, (draft) => {
                        const session = draft.sessions.find((s) => s.id === sessionId)
                        if (session) {
                          session.threadSummary = event.summary
                        }
                      })
                    )
                  }
                } catch {
                  // Skip if parsing fails
                }
              }
            })
          }
        }
        break

      case 'error':
        const errorText = event.error || 'Unknown error'
        const isDevelopmentError = errorText.startsWith('[DEV]')
        const isCreditsError = isCreditRelatedError(errorText)

        // Handle different types of development errors
        if (process.env.NODE_ENV === 'development' && !isDevelopmentError) {
          // Suppress certain development errors that should not be shown to users
          if (
            event.error === 'Duplicate message detected' ||
            event.error?.includes('Fast Refresh')
          ) {
            return
          }
        }

        // Log appropriately based on error type
        if (isCreditsError) {
          console.info('[CopilotContext] Credit-related error (not critical):', errorText)
        } else if (isDevelopmentError) {
          console.info('[CopilotContext] Development connection error:', errorText)
        } else {
          console.error('[CopilotContext] Application error:', errorText)
          // Report non-credit, non-development errors to Sentry
          const copilotError = new Error(`Copilot Streaming Error: ${errorText}`)
          ;(copilotError as any).eventId = event.id
          ;(copilotError as any).timestamp = event.timestamp
          ;(copilotError as any).isDevelopmentError = isDevelopmentError
          ;(copilotError as any).isCreditsError = isCreditsError
          ;(copilotError as any).event = event
          reporter.capture(copilotError)
        }

        // Clean up the error message for display (remove [DEV] prefix)
        const displayErrorText = isDevelopmentError ? errorText.replace('[DEV] ', '') : errorText

        // Check if this is a user cancellation - don't show error UI for cancellations
        const isCancellation = /cancelled|operation cancelled/i.test(errorText)

        if (!isCancellation) {
          // Only set error state for actual errors, not user cancellations
          dispatch(setError(displayErrorText))
        }

        dispatch(setIdle())
        // Clear active tools
        clearAllActiveToolOperations()
        break

      case 'response_complete':
        // Mark that we received response_complete (indicates natural completion)
        receivedResponseCompleteRef.current = true
        // Do not set idle here; we'll set idle on disconnect/close
        break

      case 'user_message_persisted':
        // Mark that the current user message has been persisted to backend
        setIsCurrentMessagePersisted(true)
        break

      case 'open_billing_modal':
        // Emit copilot event to open the billing modal
        const openBillingModalEvent: CopilotOpenBillingModal = event
        copilotEvents.emitOpenBillingModal(openBillingModalEvent.highlightedPlanId)
        break

      case 'open_schedule_modal':
        // Emit copilot event to open the schedule task modal
        const openScheduleModalEvent = event as CopilotOpenScheduleModal
        // Normalize null to undefined for repeatSpec inner fields
        // Backend uses .optional().nullable(), frontend uses .optional()
        const normalizedPrefill = {
          ...openScheduleModalEvent.prefill,
          description: openScheduleModalEvent.prefill.description ?? undefined,
          timezone: openScheduleModalEvent.prefill.timezone ?? undefined,
          maxRuns: openScheduleModalEvent.prefill.maxRuns ?? undefined,
          repeatSpec: openScheduleModalEvent.prefill.repeatSpec
            ? {
                kind: openScheduleModalEvent.prefill.repeatSpec.kind,
                startHour: openScheduleModalEvent.prefill.repeatSpec.startHour ?? undefined,
                interval: openScheduleModalEvent.prefill.repeatSpec.interval ?? undefined,
                daysOfWeek: openScheduleModalEvent.prefill.repeatSpec.daysOfWeek ?? undefined,
                monthlyOn: openScheduleModalEvent.prefill.repeatSpec.monthlyOn ?? undefined,
              }
            : undefined,
        }
        copilotEvents.emitOpenScheduleModal(openScheduleModalEvent.toolCallId, normalizedPrefill)
        break

      case 'agent_switched':
        console.log('[CopilotContext] Agent switched:', {
          fromAgent: event.fromAgent,
          toAgent: event.toAgent,
          reason: event.reason,
        })
        // Store agent switch info for tool completion message
        latestAgentSwitchRef.current = {
          toAgent: event.toAgent,
          fromAgent: event.fromAgent,
          timestamp: event.timestamp,
        }
        // Update current agent silently without visible messages
        setCurrentAgent(event.toAgent)
        break

      case 'disconnected':
        // Set idle on disconnect if not in error
        clearAllActiveToolOperations()
        if (!errorMessage) {
          dispatch(setIdle())
        }

        // Check if this is a natural completion (not cancelled)
        // We check both: received response_complete AND not cancelled
        const connectionStatus = serviceRef.current?.getConnectionStatus()
        const isNaturalCompletion =
          receivedResponseCompleteRef.current &&
          connectionStatus !== 'cancelling' &&
          !wasCancelledRef.current

        if (isNaturalCompletion) {
          // Count total messages across all interactions
          const totalMessageCount = interactions.reduce(
            (count, interaction) => count + interaction.messages.length,
            0
          )

          // Only play finish sound if there are at least 3 messages
          if (totalMessageCount >= 3) {
            // For particularly long interactions (10+ messages), 1/100 chance of full tune
            const isLongInteraction = totalMessageCount >= 10
            const shouldPlayFullTune = isLongInteraction && Math.random() < 0.01

            if (shouldPlayFullTune) {
              playFullTune()
            } else {
              playFinishSound()
            }
          }

          // Show browser notification if permission is granted
          showCompletionNotification(
            'Copilot finished!',
            'Your workflow has completed successfully.'
          )
        }

        // Reset flags for next session
        receivedResponseCompleteRef.current = false
        wasCancelledRef.current = false
        break
      default:
        // Handle status_change events (sent as custom events, not in CopilotResponse union)
        // Cast to unknown first since event is 'never' in the default case
        const unknownEvent = event as unknown
        if (isStatusChangeEvent(unknownEvent) && unknownEvent.status === 'cancelling') {
          wasCancelledRef.current = true
        }
        break
    }
  }

  // Store the latest handler logic in a ref
  const handlerLogicRef = useRef<null | ((event: CopilotResponse) => void)>(null)
  handlerLogicRef.current = (event: CopilotResponse) => {
    // All events go through the queue for consistent processing
    addEventToQueue(event)
  }

  // Stable event handler reference
  const stableEventHandler = useRef<(event: CopilotResponse) => void>((event: CopilotResponse) => {
    handlerLogicRef.current?.(event)
  })

  useEffect(() => {
    if (!stableEventHandler.current) return
    serviceRef.current?.onEvent(stableEventHandler.current)
    return () => {
      serviceRef.current?.offEvent(stableEventHandler.current)
    }
  }, []) // Remove serviceRef dependency as it's stable

  // Track last sent messageId for frontend idempotency
  const lastSentMessageIdRef = useRef<string | null>(null)
  const sendMessageRef = useRef<typeof sendMessage | null>(null)

  // Send a user message
  const sendMessage = useCallback(
    async (message: string, options?: SendMessageOptions) => {
      const messageIdToSend = options?.messageId || uuidv4()

      // Reset completion tracking for new message
      receivedResponseCompleteRef.current = false
      wasCancelledRef.current = false

      // Frontend idempotency: skip if this messageId was just sent
      if (lastSentMessageIdRef.current === messageIdToSend) {
        console.warn(
          '[CopilotContext] Duplicate sendMessage call detected for messageId:',
          messageIdToSend
        )
        return
      }
      lastSentMessageIdRef.current = messageIdToSend

      if (!serviceRef.current) {
        console.error('[CopilotContext] Cannot send message: service not initialized')
        dispatch(setError('Connection not ready. Please refresh the page.'))
        lastSentMessageIdRef.current = null
        return
      }

      // Complete any active typewriter animation immediately
      if (activeEventId) {
        console.log('[CopilotContext] Completing typewriter animation for:', activeEventId)
        dispatch(removeTypewriterEffect(activeEventId))
        dispatch(removeFromTypewriterQueue(activeEventId))
        setActiveEventId(null)
        isTypewriterActiveRef.current = false
      }

      // Reset persistence state for new message
      setIsCurrentMessagePersisted(false)

      // Clear any lingering active tool operations from previous interactions
      clearAllActiveToolOperations()

      dispatch(setSending())
      dispatch(clearError())

      // Prepare attachments for UI
      let fileAttachments = undefined as
        | { url: string; filename: string; ext: string }[]
        | undefined
      if (options?.fileLinks && options.fileLinks.length > 0) {
        // Use provided links directly, skip upload
        fileAttachments = options.fileLinks
        console.log('[CopilotContext] Using provided file links, skipping upload:', {
          fileCount: fileAttachments.length,
          files: fileAttachments.map((a) => ({ filename: a.filename, ext: a.ext, url: a.url })),
        })
      } else if (options?.files && options.files.length > 0) {
        console.log('[CopilotContext] Starting file upload process:', {
          fileCount: options.files.length,
          fileNames: options.files.map((f) => f.name),
          fileSizes: options.files.map((f) => f.size),
        })

        // Upload each file and collect URLs (or use pre-uploaded URLs)
        const uploadPromises = options.files.map(async (file, index) => {
          const fileWithMeta = file as any

          // If file was already uploaded, use existing URL
          if (fileWithMeta.uploadedUrl) {
            console.log(
              `[CopilotContext] Using pre-uploaded URL for file ${index + 1}/${options.files!.length}:`,
              {
                name: file.name,
                hasUrl: true,
              }
            )
            return { url: fileWithMeta.uploadedUrl, file }
          }

          // Otherwise upload now (fallback for any files that weren't pre-uploaded)
          console.log(`[CopilotContext] Uploading file ${index + 1}/${options.files!.length}:`, {
            name: file.name,
            size: file.size,
            type: file.type,
          })

          const formData = new FormData()
          formData.append('file', file)

          console.log(`[CopilotContext] FormData created for file: ${file.name}`)

          const res = await fetch(
            `${process.env.NEXT_PUBLIC_BACKEND_API_ENDPOINT_URI}/v1/services/upload-file`,
            {
              method: 'POST',
              body: formData,
              credentials: 'include',
            }
          )

          console.log(`[CopilotContext] Upload response for ${file.name}:`, {
            ok: res.ok,
            status: res.status,
            statusText: res.statusText,
          })

          if (!res.ok) {
            const errorText = await res.text()
            console.error(`[CopilotContext] File upload failed for ${file.name}:`, errorText)
            throw new Error(`File upload failed: ${res.status} ${res.statusText}`)
          }

          const data = await res.json()
          console.log(`[CopilotContext] Upload successful for ${file.name}:`, {
            hasUrl: !!data.url,
            url: data.url?.substring(0, 100) + (data.url?.length > 100 ? '...' : ''),
            dataKeys: Object.keys(data),
          })

          if (!data.url) {
            console.error(`[CopilotContext] No file URL returned for ${file.name}:`, data)
            throw new Error('No file URL returned')
          }

          return { url: data.url, file }
        })

        try {
          const uploaded = await Promise.all(uploadPromises)
          fileAttachments = uploaded.map((u) => {
            const fileWithMeta = u.file as any
            return {
              url: u.url,
              filename: u.file.name,
              ext: u.file.name.split('.').pop() || '',
              ...(fileWithMeta.imageMetadata ? { metadata: fileWithMeta.imageMetadata } : {}),
            }
          })

          console.log('[CopilotContext] All files uploaded successfully:', {
            fileCount: fileAttachments.length,
            files: fileAttachments.map((a) => ({
              filename: a.filename,
              ext: a.ext,
              hasUrl: !!a.url,
            })),
          })
        } catch (uploadError) {
          console.error('[CopilotContext] File upload process failed:', uploadError)
          throw uploadError
        }
      } else {
        console.log('[CopilotContext] No files or links provided')
      }

      const userMessage: ChatItemV2 = {
        id: messageIdToSend,
        role: 'user',
        type: 'message',
        timestamp: new Date().toISOString(),
        content: message,
        // Capture current context when message is sent
        context: {
          workflowRef: contextStateRef.current.workflowRef,
          nodeIds: contextStateRef.current.selectedNodeIds,
          ...(fileAttachments ? { files: fileAttachments } : {}),
        },
        wasEmptyContent: message.length === 0,
      }

      console.log('[CopilotContext] Created user message:', {
        id: userMessage.id,
        hasContent: !!userMessage.content,
        hasFiles: !!userMessage.context?.files,
        fileCount: userMessage.context?.files?.length || 0,
        context: userMessage.context,
      })

      // Generate interaction ID that will be used both frontend and backend
      const interactionId = `interaction_${uuidv4()}`

      // Skip adding interaction to UI for continuation requests (they resume from current state)
      if (!options?.isContinuation) {
        // Create new interaction for user message
        const newInteraction = createNewInteraction(userMessage, interactionId)
        dispatch(addInteraction(newInteraction))
      } else {
        console.log(
          '[CopilotContext] Continuation request - skipping UI update, resuming from current state'
        )
      }

      // Determine context based on workflow presence
      // If workflowRef exists, it's workflow context; otherwise copilot context
      // Agent context is when there's no workflow but a specific agent is used
      let context: 'copilot' | 'workflow' | 'agent' = 'copilot'
      if (contextStateRef.current.workflowRef) {
        context = 'workflow'
      } else if (currentAgent && currentAgent !== AgentType.META_ADS) {
        // If there's a specific agent type selected (not default) but no workflow, it's agent context
        context = 'agent'
      }

      // Track message sent event (skip for continuations)
      if (sessionId && !options?.isContinuation) {
        // Check if this is the first message (before adding, interactions array is empty)
        const isFirstMessage = interactions.length === 0

        if (isFirstMessage) {
          // Track first message separately
          analytics.copilot.firstMessageSent({
            chatId: sessionId,
            messageId: messageIdToSend,
            messageLength: message.length,
            context,
          })
        }

        // Always track regular message sent event
        analytics.copilot.messageSent({
          chatId: sessionId,
          messageId: messageIdToSend,
          messageLength: message.length,
          role: 'user',
          context,
        })
      }

      try {
        const request: CopilotSendMessageRequest = {
          id: interactionId, // Use interaction ID as request ID so backend can match them
          timestamp: userMessage.timestamp,
          message,
          sessionId: sessionId || undefined,
        }

        // Include current context in the request if available
        const requestContext: UserContext = {}

        if (contextStateRef.current.workflowRef) {
          requestContext.workflowRef = contextStateRef.current.workflowRef
        }
        // Use selectedNodeIds if available, otherwise fall back to single nodeId
        if (contextStateRef.current.selectedNodeIds?.length) {
          requestContext.nodeIds = contextStateRef.current.selectedNodeIds
        } else if (contextStateRef.current.nodeId) {
          requestContext.nodeIds = [contextStateRef.current.nodeId]
        }
        if (contextStateRef.current.selectedExecutionIds?.length) {
          requestContext.nodeExecutionIds = contextStateRef.current.selectedExecutionIds
          requestContext.executionId = contextStateRef.current.workflowExecutionId
        }
        if (fileAttachments && fileAttachments.length > 0) {
          requestContext.files = fileAttachments
          console.log('[CopilotContext] Adding files to request context:', {
            fileCount: fileAttachments.length,
            files: fileAttachments.map((f) => ({
              filename: f.filename,
              ext: f.ext,
              hasUrl: !!f.url,
            })),
          })
        }

        // Include current agent type in the context
        requestContext.agentType = currentAgent

        // Include current business profile ID for credential resolution
        if (currentBusinessProfileId) {
          requestContext.businessProfileId = currentBusinessProfileId
        }

        // If integrationSignIn is present, track newly authorized methods
        if (options?.integrationSignIn) {
          request.newlyAuthorizedMethods = [options.integrationSignIn.type]
        }

        // Mark as continuation request if specified
        if (options?.isContinuation) {
          request.isContinuation = true
          console.log('[CopilotContext] Marking request as continuation')
        }

        if (Object.keys(requestContext).length > 0) {
          request.context = requestContext
        }

        // If this is an integration sign-in and stream is active, queue it
        if (options?.integrationSignIn) {
          const connectionState = serviceRef.current?.getConnectionStatus()
          if (connectionState === 'connected' || connectionState === 'connecting') {
            console.log(
              '[CopilotContext] Stream active, queueing integration sign-in notification',
              { connectionState }
            )
            serviceRef.current?.queueMessage(request)
            console.log(
              '[CopilotContext] Integration sign-in queued, will send after stream closes'
            )
            lastSentMessageIdRef.current = null
            return
          }
        }

        await serviceRef.current?.sendMessage(request)
        console.log('[CopilotContext] Message sent successfully to service')
        // Reset lastSentMessageIdRef after successful send
        lastSentMessageIdRef.current = null
        // Reset manual agent selection flag after successful send
        hasManuallySelectedAgentRef.current = false
      } catch (err: any) {
        const errorText = err?.message || 'Failed to send message'
        const isCreditsError = isCreditRelatedError(errorText)

        // Log credit errors as info rather than error to avoid Sentry/PostHog reporting
        if (isCreditsError) {
          console.info(
            '[CopilotContext] Credit-related error during message send (not critical):',
            errorText
          )
        } else {
          console.error('[CopilotContext] Error sending message to service:', err)
        }

        // Clean up state on error - set error first, then clean up
        dispatch(setError(errorText))
        // Clear active tools on error
        clearAllActiveToolOperations()
        // Reset lastSentMessageIdRef on error as well
        lastSentMessageIdRef.current = null
        // Reset manual agent selection flag on error too
        hasManuallySelectedAgentRef.current = false

        // Re-throw the error so it can be caught by ChatInput for UI restoration
        throw err
      }
    },
    [sessionId, dispatch, contextStateRef, currentAgent, activeEventId]
  )

  // Update the ref whenever sendMessage changes
  useEffect(() => {
    sendMessageRef.current = sendMessage
  }, [sendMessage])

  // Method to update selected nodes
  const updateSelectedNodes = useCallback(
    (nodeIds: string[]) => {
      console.log('[CopilotContext] User updated selected nodes:', {
        from: contextState.selectedNodeIds,
        to: nodeIds,
      })
      safelyUpdateContextState((prev) => ({
        ...prev,
        selectedNodeIds: nodeIds,
        nodeId: nodeIds.length > 0 ? nodeIds[0] : undefined, // Keep backward compatibility
      }))
    },
    [safelyUpdateContextState]
  )

  // Method to update selected executions
  const updateSelectedExecutions = useCallback(
    (executionIds: string[]) => {
      console.log('[CopilotContext] User updated selected executions:', {
        from: contextState.selectedExecutionIds,
        to: executionIds,
      })
      safelyUpdateContextState((prev) => ({
        ...prev,
        selectedExecutionIds: executionIds,
      }))
    },
    [safelyUpdateContextState]
  )

  // Method to load node executions (for loading from IndexedDB/saved state)
  const loadNodeExecutions = useCallback(
    (nodeExecutions: Record<string, NodeExecution>) => {
      console.log('[CopilotContext] Loading node executions:', {
        count: Object.keys(nodeExecutions).length,
      })
      safelyUpdateContextState((prev) => ({
        ...prev,
        nodeExecutions: {
          ...(prev.nodeExecutions || {}),
          ...nodeExecutions,
        },
      }))
      // Also update workflow state manager
      Object.entries(nodeExecutions).forEach(([executionId, nodeExecution]) => {
        workflowState.updateNodeExecution(executionId, nodeExecution)
      })
    },
    [safelyUpdateContextState, workflowState]
  )

  // Optionally, expose a reset method
  const reset = useCallback(() => {
    dispatch(clearError())
    // Find the last user message in interactions
    let lastUserMessage: ChatItemV2User | undefined
    let targetInteractionIndex = -1

    for (let i = interactions.length - 1; i >= 0; i--) {
      const interaction = interactions[i]
      for (let j = interaction.messages.length - 1; j >= 0; j--) {
        const msg = interaction.messages[j]
        if (msg.role === 'user') {
          // No conversion needed! msg is already ChatItemV2
          lastUserMessage = msg as ChatItemV2User
          targetInteractionIndex = i
          break
        }
      }
      if (lastUserMessage) break
    }

    if (lastUserMessage && targetInteractionIndex >= 0) {
      // Keep all interactions before the one containing the last user message
      const remainingInteractions = interactions.slice(0, targetInteractionIndex)
      dispatch(setInteractions(remainingInteractions))
    }

    return lastUserMessage
  }, [dispatch, interactions])

  // Helper function for common rollback logic
  const performRollback = useCallback(
    async (
      targetInteractionId: string,
      targetInteractionIndex?: number,
      targetUserMessage?: CopilotMessageUser
    ): Promise<RollbackSessionResult | undefined> => {
      // Call backend rollback endpoint
      const response = await fetch(
        `${process.env.NEXT_PUBLIC_BACKEND_API_ENDPOINT_URI}/v2/copilot/session/${sessionId}/rollback`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify({
            interactionId: targetInteractionId,
          }),
        }
      )

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || 'Failed to rollback session')
      }

      const result = await response.json()

      const parsedResult: RollbackSessionResult = RollbackSessionResultSchema.parse(result)

      console.log('[CopilotContext] Backend rollback successful', {
        parsedResult,
      })

      // Update frontend state with the rolled back session
      if (parsedResult.session && parsedResult.session.interactions) {
        const frontendInteractions = convertBackendInteractionsToFrontend(
          parsedResult.session.interactions
        )
        dispatch(setInteractions(frontendInteractions))
      } else {
        // Fallback: manually remove interactions after the target
        if (targetInteractionIndex !== undefined) {
          const remainingInteractions = interactions.slice(0, targetInteractionIndex + 1)
          dispatch(setInteractions(remainingInteractions))
        } else {
          // For general rollback, remove the last interaction
          const remainingInteractions = interactions.slice(0, -1)
          dispatch(setInteractions(remainingInteractions))
        }
      }

      // Set the user message content and files in the chat input for editing instead of auto-resending
      // Use the target user message passed in from the frontend state (more reliable than backend response)
      const messageContent = targetUserMessage?.content || parsedResult?.targetUserMessage || ''
      // Files are stored in 'context.files' on the message
      const messageFiles = targetUserMessage?.context?.files

      console.log('[CopilotContext] Rollback - using target user message from frontend state:', {
        hasTargetMessage: !!targetUserMessage,
        content: messageContent.substring(0, 50) + (messageContent.length > 50 ? '...' : ''),
        hasContext: !!targetUserMessage?.context,
        contextKeys: targetUserMessage?.context ? Object.keys(targetUserMessage.context) : [],
        fullContext: targetUserMessage?.context,
        hasFiles: !!messageFiles,
        fileCount: messageFiles?.length || 0,
        files:
          messageFiles?.map((f: { filename: string; url: string }) => ({
            filename: f.filename,
            url: f.url.substring(0, 50) + '...',
          })) || [],
      })

      // Reset workflow state to clear thinking animation during rollback
      workflowState.resetState()

      setChatInputContent(messageContent, messageFiles)

      // Restore context state from the rolled-back session context
      // The backend provides the complete session context at the rollback point,
      // which includes workflow state, executions, and results.
      // This will trigger the workflow sync effect which loads the workflow.
      const sessionContext: CopilotSessionContext | undefined = parsedResult.session?.context
      if (sessionContext) {
        safelyUpdateContextState((prev) => ({
          ...prev,
          workflowRef: sessionContext.workflowRef || prev.workflowRef,
          workflowExecutionId: sessionContext.workflowExecutionId || undefined,
          latestResults: sessionContext.latestWorkflowResults || undefined,
          // Note: nodeId and selectedNodeIds are message-level context, not session-level,
          // so we clear them to allow the workflow to load with its default state
          nodeId: undefined,
          selectedNodeIds: undefined,
        }))

        // Execution data will be fetched automatically by the useEffect when workflowExecutionId changes

        // Handle persisted workflow results and trigger callbacks (similar to hydration logic)
        if (sessionContext.latestWorkflowResults && handleWorkflowComplete) {
          // Update Redux state with the results
          dispatch(
            setLatestResults({
              workflowRef: sessionContext.workflowRef,
              nodeResults: sessionContext.latestWorkflowResults,
              status: sessionContext.workflowExecutionStatus,
            })
          )

          // Delay the callback to ensure components are mounted
          console.log(
            '[CopilotContext] Rollback - loading workflow results',
            sessionContext.latestWorkflowResults
          )
          setTimeout(() => {
            handleWorkflowComplete(
              {
                workflowId: sessionContext.workflowRef,
                nodeResults: sessionContext.latestWorkflowResults,
                status: sessionContext.workflowExecutionStatus,
              },
              sessionContext.latestExecutionId
            )
          }, 100)
        }

        // Handle workflow execution status for errors during rollback
        if (
          sessionContext.workflowExecutionStatus &&
          sessionContext.workflowExecutionStatus !== 'completed' &&
          handleWorkflowError
        ) {
          const errorMessage = `Workflow execution ended with status: ${sessionContext.workflowExecutionStatus}`
          setTimeout(() => {
            handleWorkflowError(errorMessage)
          }, 100)
        }
      } else {
        // Clear context if no session context available
        safelyUpdateContextState(() => ({}))
      }

      // Convert backend user message to frontend format
      return parsedResult
    },
    [dispatch, interactions, sessionId]
  )

  // Rollback to a specific interaction by message ID
  const rollbackToMessage = useCallback(
    async (messageId: string): Promise<RollbackSessionResult | undefined> => {
      dispatch(clearError())

      if (interactions.length === 0 || !sessionId) return undefined

      // Find the interaction that contains this message
      const targetInteractionIndex = interactions.findIndex((interaction) =>
        interaction.messages.some((msg) => msg.id === messageId)
      )

      if (targetInteractionIndex === -1) {
        console.error('[CopilotContext] Cannot find interaction containing message:', messageId)
        return undefined
      }

      const targetInteraction = interactions[targetInteractionIndex]
      const userMessage = targetInteraction.messages.find(
        (msg): msg is ChatItemV2User => msg.role === 'user'
      )

      if (!userMessage) return undefined

      try {
        console.log('[CopilotContext] Rolling back to interaction', {
          sessionId,
          messageId,
          targetInteractionId: targetInteraction.id,
          targetInteractionIndex,
          totalInteractions: interactions.length,
          targetUserMessage: {
            id: userMessage.id,
            content: userMessage.content?.substring(0, 50),
            hasContext: !!userMessage.context,
            contextKeys: userMessage.context ? Object.keys(userMessage.context) : [],
            contextFiles: userMessage.context?.files,
            files: userMessage.context?.files,
            fullMessage: userMessage,
          },
        })

        return await performRollback(targetInteraction.id, targetInteractionIndex, userMessage)
      } catch (error) {
        console.error('[CopilotContext] Failed to rollback interaction:', error)
        // Fallback to local rollback if backend fails
        const remainingInteractions = interactions.slice(0, targetInteractionIndex + 1)
        dispatch(setInteractions(remainingInteractions))

        // Reset workflow state to clear thinking animation during rollback
        workflowState.resetState()

        // Still set the user message content and files in the chat input for editing
        // Files are stored in 'context.files' on the message
        setChatInputContent(userMessage.content || '', userMessage.context?.files)

        // In fallback case, we don't have session context from backend
        // So we'll clear execution state but preserve workflow reference if available from message
        const messageContext = userMessage.context
        if (messageContext?.workflowRef) {
          safelyUpdateContextState((prev) => ({
            ...prev,
            workflowRef: messageContext.workflowRef,
            // Clear other state since we don't have proper session context
            nodeId: undefined,
            selectedNodeIds: undefined,
            nodeExecutions: undefined,
            latestResults: undefined,
          }))
        } else {
          // Clear context completely if no workflow reference available
          safelyUpdateContextState(() => ({}))
        }

        return undefined
      }
    },
    [dispatch, interactions, sessionId, performRollback]
  )

  // Function to set chat input content (used by rollback)
  const setChatInputContent = useCallback(
    (content: string, files?: { url: string; filename: string; ext: string }[]): void => {
      // Additional null safety since we're seeing null values despite schema expecting strings
      const safeContent = content != null ? String(content) : ''

      console.log('[CopilotContext] setChatInputContent called:', {
        content: safeContent.substring(0, 50) + (safeContent.length > 50 ? '...' : ''),
        hasFiles: !!files,
        fileCount: files?.length || 0,
        files:
          files?.map((f: { filename: string; url: string }) => ({
            filename: f.filename,
            url: f.url.substring(0, 50) + '...',
          })) || [],
      })

      // Emit rollback event to chat input components with content and files
      copilotEvents.emitRollback(safeContent, files)
    },
    []
  )

  // Hydrate chat from backend for existing conversations only
  useEffect(() => {
    console.log('[CopilotContext] Hydration effect running', {
      sessionId,
      conversationId,
      isUserLoading,
    })
    async function hydrateFromBackend(sessionId: string) {
      console.log('[CopilotContext] hydrateFromBackend called', { sessionId })
      try {
        const res = await fetch(
          `${process.env.NEXT_PUBLIC_BACKEND_API_ENDPOINT_URI}/v2/copilot/session/${sessionId}`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
            },
            credentials: 'include',
          }
        )
        if (res.status === 404) {
          // No session found, treat as new chat - ensure clean state
          console.debug(
            '[CopilotContext] Session not found (404), ensuring clean state for new chat'
          )
          dispatch(resetState())
          setHasHydrated(true)
          return
        }
        if (!res.ok) return
        const data = await res.json()

        if (data && data.session) {
          const session = data.session satisfies CopilotSession

          // Track opening existing chat (only if it's not a new/empty session)
          // A session is considered "existing" if it has interactions
          const hasInteractions =
            Array.isArray(session.interactions) && session.interactions.length > 0
          if (hasInteractions) {
            // Determine source based on URL parameters and referrer
            let source = 'direct_link' // default
            if (typeof window !== 'undefined') {
              const urlParams = new URLSearchParams(window.location.search)
              const urlSource = urlParams.get('source')
              if (urlSource) {
                source = urlSource
              } else {
                // Determine source from referrer
                const referrer = document.referrer
                if (referrer.includes('/threads')) {
                  source = 'threads_page'
                } else if (referrer.includes('/copilot') || referrer.includes('/dashboard')) {
                  source = 'sidebar'
                }
              }
            }
            analytics.copilot.userOpenedChat(sessionId, source)
          }

          // Handle session context
          if (session.context) {
            console.log('[CopilotContext] Session hydration found context', {
              sessionContext: session.context,
              sessionId,
              timestamp: new Date().toISOString(),
              contextKeys: Object.keys(session.context),
              hasWorkflowExecutionId: !!(session.context as any).workflowExecutionId,
              hasLatestExecutionId: !!(session.context as any).latestExecutionId,
              hasNodeExecutions: !!(session.context as any).nodeExecutions,
            })
            updateSessionContext(session.context)

            // Handle persisted workflow results and trigger callbacks
            if (session.context?.latestWorkflowResults && handleWorkflowComplete) {
              // Delay the callback to ensure components are mounted
              console.log(
                '[CopilotContext] Setting workflow results',
                session.context.latestWorkflowResults
              )
              setTimeout(() => {
                handleWorkflowComplete(
                  {
                    workflowId: session.context?.workflowRef,
                    nodeResults: session.context?.latestWorkflowResults,
                    status: session.context?.workflowExecutionStatus,
                  },
                  session.context?.latestExecutionId
                )
              }, 100)
            }

            // Handle workflow execution status for errors
            if (
              session.context?.workflowExecutionStatus &&
              session.context.workflowExecutionStatus !== 'completed' &&
              handleWorkflowError
            ) {
              const errorMessage = `Workflow execution ended with status: ${session.context.workflowExecutionStatus}`
              setTimeout(() => {
                handleWorkflowError(errorMessage)
              }, 100)
            }
          }

          // Handle agent type - ALWAYS prioritize backend session state for state synchronization
          const backendAgentType = session.currentAgentType

          // Priority order:
          // 1. Backend session has the definitive agent type
          // 2. Query parameter as fallback (for direct links)
          // 3. Default to META_ADS

          let finalAgentType = backendAgentType
          if (!finalAgentType) {
            // Check query parameters as fallback
            const urlParams = new URLSearchParams(window.location.search)
            const queryAgentType = urlParams.get('agentType') as AgentType
            if (queryAgentType && ['seo', 'meta_ads', 'google_ads'].includes(queryAgentType)) {
              finalAgentType = queryAgentType
            } else {
              finalAgentType = AgentType.META_ADS
            }
          }

          console.log('[CopilotContext] Setting agent type from backend session:', {
            backendAgentType,
            finalAgentType,
            sessionId,
          })
          setCurrentAgent(finalAgentType)

          // Handle interactions - use directly instead of flattening
          if (Array.isArray(session.interactions)) {
            console.log(
              '[CopilotContext] Hydrated interactions from backend',
              session.interactions.map((i: CopilotInteraction) => i.id)
            )

            // Filter out interactions with empty or no messages
            const validInteractions = session.interactions.filter(
              (interaction: CopilotInteraction) =>
                interaction.messages && interaction.messages.length > 0
            )

            // Log filtered interactions
            const filteredInteractions = session.interactions.filter(
              (interaction: CopilotInteraction) =>
                !interaction.messages || interaction.messages.length === 0
            )
            if (filteredInteractions.length > 0) {
              console.log('[CopilotContext] Filtered out empty interactions', {
                count: filteredInteractions.length,
                interactionIds: filteredInteractions.map((i: CopilotInteraction) => i.id),
              })
            }

            // Only replace local interactions if backend has interactions to hydrate
            // This prevents clearing local interactions when backend returns empty array for new sessions
            if (validInteractions.length > 0) {
              console.log('[CopilotContext] Hydrating interactions from backend', {
                totalInteractions: session.interactions.length,
                validInteractionsCount: validInteractions.length,
                rawMessages: validInteractions.flatMap((i: CopilotInteraction) => i.messages),
                allInteractionDetails: validInteractions.map((i: CopilotInteraction) => ({
                  id: i.id,
                  messageCount: i.messages.length,
                  messageRoles: i.messages.map((m: CopilotMessage) => m.role),
                })),
                toolMessages: validInteractions
                  .flatMap((i: CopilotInteraction) => i.messages)
                  .filter((m: CopilotMessage) => m.role === 'tool'),
              })
              const frontendInteractions = convertBackendInteractionsToFrontend(validInteractions)
              console.log('[CopilotContext] Converted frontend interactions', {
                frontendInteractionsCount: frontendInteractions.length,
                interactionDetails: frontendInteractions.map((i) => ({
                  id: i.id,
                  messageCount: i.messages.length,
                  messageRoles: i.messages.map((m: any) => m.role),
                })),
                toolProgressItems: frontendInteractions
                  .flatMap((i) => i.messages)
                  .filter((m: any) => m.role === 'tool'),
              })
              dispatch(setInteractions(frontendInteractions))

              // Rebuild workflowExecutions from interactions
              // Iterate backwards from newest to oldest
              const executionIds = new Set<string>()
              for (let i = validInteractions.length - 1; i >= 0; i--) {
                const interaction = validInteractions[i]

                // Collect execution IDs from this interaction
                if (interaction.context?.workflowExecutionId) {
                  executionIds.add(interaction.context.workflowExecutionId)
                }
              }

              if (executionIds.size > 0) {
                console.log('[CopilotContext] Rebuilding workflowExecutions from interactions', {
                  executionIds: Array.from(executionIds),
                })

                // Fetch execution data for each unique execution ID
                Array.from(executionIds).forEach((executionId) => {
                  fetchExecutionDataIfNeeded(executionId, 'hydration')
                })
              }
            }
          }
        }
      } catch (err: any) {
        console.error('Error hydrating from backend:', err)
      } finally {
        setHasHydrated(true)
      }
    }

    // Don't attempt to fetch if user data is still loading
    if (isUserLoading) {
      return
    }

    // Only hydrate if we have both sessionId AND conversationId (existing conversation)
    if (sessionId && conversationId) {
      if (!workspaceId) {
        console.warn(
          '[CopilotContext] Skipping hydration - workspaceId is unavailable, likely due to missing auth context'
        )
        setHasHydrated(true)
        return
      }

      hydrateFromBackend(sessionId)
      return
    }

    // For new chats, just mark as hydrated without fetching
    setHasHydrated(true)
  }, [sessionId, conversationId, dispatch, updateSessionContext, workspaceId, isUserLoading])

  // Handle initial message separately to avoid dependency issues
  useEffect(() => {
    if (hasHydrated && sessionId && conversationId && !isUserLoading) {
      console.log('[CopilotContext] Checking for initial message:', {
        sessionId,
        conversationId,
        hasHydrated,
        isUserLoading,
      })

      let timeoutId: NodeJS.Timeout | undefined

      // Load initial message asynchronously from IndexedDB
      getInitialMessage(sessionId)
        .then((stored) => {
          const hasMessage = stored?.message && stored.message.length > 0
          const hasFiles = stored?.fileLinks && stored.fileLinks.length > 0
          const hasAgentType = !!stored?.agentType

          if (stored && (hasMessage || hasFiles || hasAgentType)) {
            // Use a timeout to ensure hydration is complete
            timeoutId = setTimeout(async () => {
              try {
                // Set agent type from stored initial message if provided
                if (stored.agentType) {
                  console.log(
                    '[CopilotContext] Setting agent type from initial message:',
                    stored.agentType
                  )
                  setCurrentAgent(stored.agentType)
                }

                // Only send message if there's actual content to send
                if (hasMessage || hasFiles) {
                  const initialMessageId = `initial_${sessionId}`

                  if (sendMessageRef.current) {
                    // For initial messages, we can use the regular sendMessage function
                    // since it will handle file uploads properly
                    const messageOptions: SendMessageOptions = {
                      ...stored.options,
                      messageId: initialMessageId,
                      fileLinks: stored.fileLinks,
                    }

                    await sendMessageRef.current(stored.message, messageOptions)
                    console.log('[CopilotContext] Initial message sent successfully with links:', {
                      hasLinks: !!stored.fileLinks,
                      linkCount: stored.fileLinks?.length || 0,
                      fileNames: stored.fileLinks?.map((f) => f.filename) || [],
                      agentType: stored.agentType,
                    })
                  } else {
                    console.error(
                      '[CopilotContext] sendMessageRef.current is null, cannot send initial message'
                    )
                  }
                } else {
                  console.log('[CopilotContext] Agent type set, no message to send:', {
                    agentType: stored.agentType,
                  })
                }

                await removeInitialMessage(sessionId)
              } catch (e) {
                console.error('[CopilotContext] Failed to send initial message from storage', e)
                await removeInitialMessage(sessionId)
              }
            }, 100)
          } else {
            console.log('[CopilotContext] No initial message to send:', {
              sessionId,
              hasStored: !!stored,
              hasMessage: !!stored?.message,
              hasLinks: !!stored?.fileLinks && stored.fileLinks.length > 0,
              fileCount: stored?.fileLinks?.length || 0,
            })
          }
        })
        .catch((error) => {
          console.error('[CopilotContext] Failed to load initial message:', error)
        })

      return () => {
        if (timeoutId) {
          clearTimeout(timeoutId)
        }
      }
    }
  }, [hasHydrated, sessionId, conversationId, isUserLoading])

  // Listen for workflow deletion events from the API layer
  useEffect(() => {
    const handleWorkflowDeleted = (event: CustomEvent) => {
      const { workflowId } = event.detail
      console.log('[CopilotContext] Workflow deleted event received:', { workflowId })

      // Check if the deleted workflow matches the current context
      if (contextState.workflowRef) {
        // Compare base workflow IDs (without version suffixes)
        const currentBaseId = contextState.workflowRef.replace(/^workflow_/, '').replace(/:.*$/, '')
        const deletedBaseId = workflowId.replace(/^workflow_/, '').replace(/:.*$/, '')

        if (currentBaseId === deletedBaseId) {
          console.log('[CopilotContext] Clearing context for deleted workflow:', {
            currentWorkflowRef: contextState.workflowRef,
            deletedWorkflowId: workflowId,
          })

          // Clear the workflow context
          safelyUpdateContextState((prev) => ({
            ...prev,
            workflowRef: undefined,
            nodeId: undefined,
            selectedNodeIds: undefined,
            selectedExecutionIds: undefined,
            nodeExecutions: undefined,
            latestResults: undefined,
            workflowExecutionId: undefined,
            htmlFullscreen: undefined,
          }))

          // Also clear the workflow state
          workflowState.resetState()
        }
      }
    }

    if (typeof window !== 'undefined') {
      window.addEventListener('workflowDeleted', handleWorkflowDeleted as EventListener)
      return () => {
        window.removeEventListener('workflowDeleted', handleWorkflowDeleted as EventListener)
      }
    }
  }, [contextState.workflowRef, safelyUpdateContextState, workflowState])

  // Helper function to normalize workflow ID for comparison (remove version suffix)
  const normalizeWorkflowId = (workflowId: string | undefined): string => {
    if (!workflowId) return ''
    // Remove workflow_ prefix and :version suffix to get base ID
    const baseId = workflowId
      .replace(/^workflow_/, '') // Remove workflow_ prefix
      .replace(/:.*$/, '') // Remove :version suffix
    return baseId
  }

  // Sync workflow state with copilot context - only when workflowId changes
  const syncingRef = useRef(false)
  const lastSyncedValueRef = useRef<string>('')

  useEffect(() => {
    const contextNormalized = normalizeWorkflowId(contextState.workflowRef)
    const workflowStateNormalized = normalizeWorkflowId(workflowState.workflowRef)
    const needsSync = contextNormalized !== workflowStateNormalized && contextNormalized !== ''

    // Prevent syncing to the same value repeatedly
    const alreadySynced = lastSyncedValueRef.current === contextState.workflowRef

    console.log('[CopilotContext] Workflow sync effect triggered', {
      contextWorkflowRef: contextState.workflowRef,
      workflowStateWorkflowRef: workflowState.workflowRef,
      contextNormalized,
      workflowStateNormalized,
      needsSync,
      alreadySynced,
      isCurrentlysyncing: syncingRef.current,
      timestamp: new Date().toISOString(),
    })

    if (needsSync && !syncingRef.current && !alreadySynced) {
      console.log('[CopilotContext] Syncing workflowState with contextState', {
        from: workflowState.workflowRef,
        to: contextState.workflowRef,
      })

      syncingRef.current = true
      lastSyncedValueRef.current = contextState.workflowRef || ''

      workflowState.updateContext({
        workflowRef: contextState.workflowRef,
        nodeId: contextState.nodeId,
        selectedNodeIds: contextState.selectedNodeIds,
        nodeExecutions: contextState.nodeExecutions,
      })

      // Reset sync flag after state has had a chance to update
      setTimeout(() => {
        syncingRef.current = false
      }, 0)
    }
  }, [
    contextState.workflowRef,
    workflowState.workflowRef,
    contextState.nodeId,
    contextState.selectedNodeIds,
    contextState.nodeExecutions,
  ])

  // Sync Redux workflow execution state with unified state
  useEffect(() => {
    if (isWorkflowExecuting && !workflowState.isExecuting) {
      // Redux says we're executing but unified state doesn't - sync it
      const workflowRef = contextState.workflowRef || 'unknown'
      workflowState.startExecution(workflowRef, 'Workflow execution started')
    } else if (
      !isWorkflowExecuting &&
      workflowState.isExecuting &&
      workflowState.phase === 'executing'
    ) {
      // Redux says we're not executing but unified state thinks we are - check for completion
      if (contextState.latestResults) {
        handleWorkflowComplete(contextState.latestResults, contextState.workflowExecutionId)
      }
    }
  }, [
    isWorkflowExecuting,
    workflowState.isExecuting,
    workflowState.phase,
    contextState.workflowRef,
    contextState.latestResults,
  ])

  const setWorkflowPreviewRef = (ref: any) => {
    workflowPreviewRef.current = ref

    // Process any pending workflow actions now that ref is available
    if (ref && pendingWorkflowActionsRef.current.length > 0) {
      console.log('[CopilotContext] Processing pending workflow actions', {
        count: pendingWorkflowActionsRef.current.length,
      })

      pendingWorkflowActionsRef.current.forEach((action) => {
        if (action.type === 'complete') {
          ref.completeWorkflow(action.data)
        } else if (action.type === 'error') {
          ref.errorWorkflow(action.data)
        }
      })

      pendingWorkflowActionsRef.current = []
    }
  }

  const updateWorkflowRef = useCallback(
    (workflowRef: WorkflowRefStrict) => {
      console.log('[CopilotContext] Updating workflowRef:', {
        from: contextState.workflowRef,
        to: workflowRef,
      })
      safelyUpdateContextState((prev) => ({
        ...prev,
        workflowRef,
      }))
    },
    [contextState.workflowRef, safelyUpdateContextState]
  )

  const setHtmlFullscreen = useCallback(
    (data: { content?: string; url?: string; filename: string }) => {
      console.log('[CopilotContext] Setting HTML fullscreen:', data)
      safelyUpdateContextState((prev) => ({
        ...prev,
        htmlFullscreen: data,
      }))
    },
    [safelyUpdateContextState]
  )

  const clearHtmlFullscreen = useCallback(() => {
    console.log('[CopilotContext] Clearing HTML fullscreen')
    safelyUpdateContextState((prev) => ({
      ...prev,
      htmlFullscreen: undefined,
    }))
  }, [safelyUpdateContextState])

  const clearAllResults = useCallback(() => {
    console.log('[CopilotContext] Clearing all results')
    // Clear workflow state manager
    workflowState.clearNodeExecutions()
    // Clear fetched execution IDs to allow re-fetching if same workflows run again
    fetchedExecutionIdsRef.current.clear()
    // Clear context state
    safelyUpdateContextState((prev) => ({
      ...prev,
      nodeExecutions: {},
      latestResults: undefined,
      workflowExecutions: {},
    }))
  }, [workflowState, safelyUpdateContextState])

  const deleteSession = useCallback(async () => {
    if (!sessionId) return

    try {
      const res = await fetch(
        `${process.env.NEXT_PUBLIC_BACKEND_API_ENDPOINT_URI}/v2/copilot/session/${sessionId}`,
        {
          method: 'DELETE',
          credentials: 'include',
        }
      )

      if (res.ok) {
        router.push('/copilot')
      }
    } catch (err) {
      console.error('[CopilotContext] Failed to delete session:', err)
    }
  }, [sessionId, router])

  const updateTypewriterProgress = useCallback(
    (messageId: string, characterIndex: number) => {
      // Update the message in Redux store
      dispatch(
        updateTypewriterState({
          messageId: messageId,
          characterIndex: characterIndex,
        })
      )
    },
    [dispatch]
  )

  const completeTypewriterAnimation = useCallback(
    (eventId: string) => {
      // Clear active event and resume queue processing
      setActiveEventId(null)
      isTypewriterActiveRef.current = false

      // Remove typewriter effect from the message
      dispatch(removeTypewriterEffect(eventId))

      // Remove from Redux state and event queue
      dispatch(removeFromTypewriterQueue(eventId))
      removeEventFromQueue(eventId)

      // Clean up tracking
      processedEventRef.current.delete(eventId)

      // Don't set idle here; idle should be set when SSE disconnects

      // Don't auto-scroll after typewriter completes - let user control scrolling
      // Only scroll when user explicitly submits a message

      // Resume processing any queued events
      setTimeout(processQueuedEvents, 10)
    },
    [dispatch]
  )

  // Memoize the context object to prevent unnecessary re-renders
  const memoizedContext = useMemo(() => {
    return {
      ...contextState,
      latestResults: latestResults, // Include Redux state in context
    }
  }, [contextState, latestResults])

  // Agent switching function implementation - frontend only
  const switchAgent = useCallback(
    async (targetAgent: AgentType, reason?: string): Promise<boolean> => {
      console.log('[CopilotContext] switchAgent called:', {
        currentAgent,
        targetAgent,
        reason,
        timestamp: new Date().toISOString(),
      })

      if (currentAgent === targetAgent) {
        console.log('[CopilotContext] Agent already set to target:', targetAgent)
        return true
      }

      console.log('[CopilotContext] Switching agent (frontend only):', {
        from: currentAgent,
        to: targetAgent,
        reason,
      })

      // Mark that user has manually selected an agent
      hasManuallySelectedAgentRef.current = true

      // Update agent immediately - no backend call needed
      setCurrentAgent(targetAgent)

      // Log after state update (note: state might not be immediately updated due to React batching)
      console.log('[CopilotContext] setCurrentAgent called, state should update to:', targetAgent)

      return true
    },
    [currentAgent]
  )

  const contextValue = {
    sendMessage,
    messages,
    status,
    error: errorMessage,
    reset,
    rollbackToMessage,
    setChatInputContent,
    sessionId,
    hasHydrated,
    deleteSession,
    // Agent management
    currentAgent,
    switchAgent,
    availableAgents: AvailableAgents.filter((agent) => WHITELISTED_AGENTS.includes(agent.type)),
    context: memoizedContext,
    updateSelectedNodes,
    updateSelectedExecutions,
    updateWorkflowRef,
    loadNodeExecutions,
    setHtmlFullscreen,
    clearHtmlFullscreen,
    clearAllResults,
    isLoading: isUserLoading,
    setWorkflowPreviewRef,
    activeTypewriterMessageId: activeEventId,
    startTypewriterAnimation: setActiveEventId,
    updateTypewriterProgress,
    completeTypewriterAnimation,
    isWorkflowExecuting,
    // Event queue management
    eventQueue: eventQueueRef.current,
    processNextEvent,
    clearEventQueue,
    // Enhanced workflow state management
    workflowState,
    // Backward compatibility - computed from workflowState
    hasActiveWorkflowExecutions: workflowState.hasActiveWorkflowExecutions,
    showThinkingAnimation: workflowState.showThinkingAnimation,
    // Legacy workflow execution state
    workflowExecution,
    // Track if current user message was persisted to backend
    isCurrentMessagePersisted,
    // Track if there are active tool operations for interaction-level thinking animation
    hasActiveTools,
    handleWorkflowComplete,
    onOpenPanel,
  }

  return <CopilotContext.Provider value={contextValue}>{children}</CopilotContext.Provider>
}

export function useCopilotContext() {
  const context = useContext(CopilotContext)
  if (!context) {
    throw new Error('useCopilotContext must be used within a CopilotProvider')
  }
  return context
}
